// Phaser シーン: 棒人間の描画、入力 (キーボード + クリック/タップ目的地)、
// 自分の物理ボディ、机/壁との衝突、ピア棒人間 (物理なし、entity interpolation 補間)、
// 発話リング、ゾーン overlap 検知、スピーカーの装飾描画。
//
// 視点モード切替 (俯瞰 / iso) に備えて toScreen / fromScreen / rotateInput を抽象化。
// 物理 body と論理座標 (net.ts / live.ts に渡す値) は常に俯瞰系。
// 現状は VIEW_MODE = "topdown" 固定。

import Phaser from "phaser";

import { AVATARS, getAvatarForPeerId } from "./avatars";
import type { Live } from "./live";
import { MAP, SWEETSPOT, findActiveZone, type Zone } from "./map";
import type { Net, Peer } from "./net";

const MOVE_SPEED = 180;
const SEND_INTERVAL_MS = 50;
const ARRIVAL_THRESHOLD = 4;
/** 障害物の「前面」が画面下方向に張り出す高さ (= side-view の「厚み」) */
const FRONT_DEPTH = 18;

/**
 * ピア座標の entity interpolation 設定。
 * FPS / MMO 標準パターン: 直近 snapshot を時系列で保持し、`renderTime = now - DELAY`
 * の時刻に該当する位置を 2 snapshot から線形補間して描画する。
 * 100ms の遅延と引き換えに network jitter (peer-moved の到着間隔ぶれ) を吸収する。
 */
const INTERP_DELAY_MS = 100;
/** snapshot は最近 1 秒ぶんあれば十分。それ以上古いものは捨てる */
const INTERP_BUFFER_MS = 1000;
/**
 * renderTime が buffer 最新 snapshot より先 (= 想定 jitter ぶん追いついていない) のとき、
 * 直近 2 snapshot の速度で外挿 (dead reckoning) する上限時間。
 * 80ms cap は「peer 切断で freeze するケース」と「jitter スパイクで一瞬遅れたケース」を
 * 区別するための妥協値。これ以上外挿するとテレポート見えになる。
 */
const EXTRAPOLATION_CAP_MS = 80;

/** soft peer collision: 自分の半径以内に peer が来たら反発 force を加える */
const PEER_COLLIDE_RADIUS = 28;
const PEER_COLLIDE_PUSH = 140;
/**
 * velocity ベースの押し返し合計を MOVE_SPEED の半分で頭打ちにする。
 * 悪意ある peer が自分の座標に重ねた move を spam した場合 (= 「ストーカー」攻撃) に、
 * 反発が累積してフラフラ揺さぶられるのを抑える。
 */
const PEER_PUSH_CLAMP = MOVE_SPEED * 0.5;
/**
 * position resolution の overlap 補正係数。両者が距離 = R に収束する標準テクニックは
 * 0.5 ずつ動かすが、片側 (自分) しか動かせないので 1.0 ぶん補正する設計もある。
 * ここでは「peer が複数いた場合に振動しないよう」やや控えめの 0.5 とし、
 * フレーム間で漸近させる。
 */
const PEER_RESOLVE_FACTOR = 0.5;

/** spawn collision avoidance: 既存 peer の半径以内なら一度だけ自動でずらす */
const SPAWN_AVOID_RADIUS = 50;
const SPAWN_AVOID_STEP = 60;

/** target movement の諦め判定: 何かに継続的にぶつかっていて到達できないなら target を null に */
const PATHFINDING_STUCK_MS = 150;

/** target movement で「ARRIVAL_THRESHOLD まで来ていないが進行不能」を判定するための速度しきい値 */
const STUCK_SPEED_EPS = 2;

type ViewMode = "topdown" | "iso";
const VIEW_MODE: ViewMode = "topdown";
const TILE_W = 64;
const TILE_H = 32;

function toScreen(x: number, y: number): { sx: number; sy: number } {
  if (VIEW_MODE === "iso") {
    return { sx: (x - y) * (TILE_W / 2), sy: (x + y) * (TILE_H / 2) };
  }
  return { sx: x, sy: y };
}

function fromScreen(sx: number, sy: number): { x: number; y: number } {
  if (VIEW_MODE === "iso") {
    const x = sx / (TILE_W / 2) / 2 + sy / (TILE_H / 2) / 2;
    const y = sy / (TILE_H / 2) / 2 - sx / (TILE_W / 2) / 2;
    return { x, y };
  }
  return { x: sx, y: sy };
}

function rotateInput(dx: number, dy: number): { dx: number; dy: number } {
  if (VIEW_MODE === "iso") {
    const inv = 1 / Math.SQRT2;
    return { dx: (dx - dy) * inv, dy: (dx + dy) * inv };
  }
  return { dx, dy };
}

interface FigureContainer extends Phaser.GameObjects.Container {
  ring: Phaser.GameObjects.Graphics;
  labelText: Phaser.GameObjects.Text;
  /** 自分のみに付く足元 halo (peer なら null) */
  halo: Phaser.GameObjects.Graphics | null;
}

/** peer ごとの interpolation snapshot リングバッファ要素 */
interface Snapshot {
  t: number;
  x: number;
  y: number;
}

export interface ClubSceneDeps {
  net: Net;
  live: Live;
  onZoneChange: (zone: Zone | null) => void;
  /** 自分が入室前に選んだ avatar key。null/省略時は決定論的 hash で割り当て (= 既存挙動)。
   *  peer 側は引き続き hash 決定論なので、この PR では「自分の見え方だけ」を選択できる。 */
  selfAvatarKey?: string | null;
  /** 入室前に localStorage から読み出した照明モード初期値。
   *  これを constructor で `this.lightingMode` に焼き込むことで、Phaser scene の
   *  create() が走るより前に setupLightingButton が `getLightingMode()` を読んでも
   *  正しい値を返せる (= 「OFF にしたはずなのに button が ON 表示」みたいな
   *  init race を防ぐ)。 */
  initialLightingMode?: "off" | "lite" | "full";
}

export class ClubScene extends Phaser.Scene {
  private deps: ClubSceneDeps;
  private me!: FigureContainer & { body: Phaser.Physics.Arcade.Body };
  private figures = new Map<string, FigureContainer>();
  /** peer ごとに直近の (t, x, y) snapshot を保持。最古→最新の順 */
  private peerBuffers = new Map<string, Snapshot[]>();
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key };
  private obstacles!: Phaser.Physics.Arcade.StaticGroup;
  private target: { x: number; y: number } | null = null;
  private targetMarker: Phaser.GameObjects.Arc | null = null;
  private lastSendAt = 0;
  private currentZone: Zone | null = null;
  /** target に向かっている間 body.blocked.any が連続している経過時間 (ms) */
  private stuckMs = 0;
  /** スポーン時 1 度だけの避け移動を行ったか */
  private spawnAvoided = false;
  /** カメラズーム倍率 (= モバイル向け虫眼鏡ボタンで増減)。1.0 が等倍。 */
  private cameraZoom = 1.0;
  private static readonly ZOOM_MIN = 0.6;
  private static readonly ZOOM_MAX = 1.8;
  private static readonly ZOOM_STEP = 0.15;

  // ─── reactive lighting (= 音域反応 floor 照明) ────────────────────────
  // 光過敏症対策で **default OFF**。setLightingMode("lite"|"full") で opt-in。
  // モード:
  //  - "off":  全効果停止、FFT 読みもスキップ (= CPU/GPU コストゼロ)
  //  - "lite": speaker pulses のみ (= モバイル向け軽量、transient 効果)
  //  - "full": speaker pulses + spotlights + mirror ball (= デスクトップ向けフル)
  // 注: 旧 v2 にあった「フルスクリーン color overlay (darken/bass/mid/high)」は
  // 視覚的に flash が強く「うるさい」体感だったので削除。floor 領域に限定した
  // 動的効果 (spot / mirror / pulse) だけ残す。
  private lightingMode: "off" | "lite" | "full" = "off";
  /** band level の envelope state (= rise immediate, decay slow)。
   *  spotlight / mirror / pulse の駆動値として使う (= overlay 用ではなくなった)。 */
  private bandEnv = { bass: 0, mid: 0, high: 0, master: 0 };
  /** AnalyserNode 用の事前確保バッファ (= frequencyBinCount 一致時のみ再利用)。 */
  private analyserBuf: Uint8Array<ArrayBuffer> | null = null;
  /** envelope の 1 frame あたり decay 比 (= 値 × LIGHTING_DECAY → 次フレーム)。
   *  0.88 ≒ 50% 減衰に 5 フレーム ≒ 83ms。クラブ照明の慣性として自然。 */
  private static readonly LIGHTING_DECAY = 0.88;

  // ─── v2: スピーカーパルス / スポットライト / ミラーボール ─────────────
  /** 前フレームの band envelope (= rising-edge 検出に使う、speaker パルス trigger)。 */
  private prevBandEnv = { bass: 0, mid: 0, high: 0 };
  /** speaker パルスを spawn する閾値 (= band envelope の rising edge の最低強度)。
   *  これより小さい急上昇は無視して、過剰な spam を防ぐ。 */
  private static readonly PULSE_TRIGGER_THRESHOLD = 0.35;
  /** 1 つのスピーカーから連続して pulse を spawn できる最小間隔 (ms)。
   *  bass の連打で 1 speaker から 60 個の pulse が出ないようにする rate limit。 */
  private static readonly PULSE_MIN_INTERVAL_MS = 120;
  /** 直近 spawn 時刻を speaker id ごとに記録 (= rate limit 用)。 */
  private pulseLastSpawnAt: Record<string, number> = {};
  /** sweep するスポットライト ellipse 群 (= フロア上空を回る照明)。Full mode のみ表示。 */
  private spotlights: Phaser.GameObjects.Ellipse[] = [];
  /** スポットライト個数。多すぎると視覚が忙しい。 */
  private static readonly SPOTLIGHT_COUNT = 4;
  /** スポットライト sweep 1 周期 (ms)。長いとゆったり、短いとせわしない。 */
  private static readonly SPOTLIGHT_SWEEP_PERIOD_MS = 9000;
  /** ミラーボール反射光 dot 群 (= フロア中心を回る小光点)。Full mode のみ。 */
  private mirrorDots: Phaser.GameObjects.Arc[] = [];
  /** ミラーボール dot 個数 (= 多すぎると GPU 重い、20-30 が現実的)。 */
  private static readonly MIRROR_DOT_COUNT = 24;
  /** ミラーボール回転 1 周期 (ms)。 */
  private static readonly MIRROR_ROTATION_PERIOD_MS = 7000;
  /** floor 領域 (= MAP.zones の "floor" rect) にすべての lighting effects をクリップ
   *  するための共有 mask。speaker pulse / spotlight / mirror dot が floor 外にハミ出る
   *  時に物理的に切り取る。overlay 4 枚は world rect サイズで定義するので mask 不要。 */
  private floorLightingMask: Phaser.Display.Masks.GeometryMask | null = null;

  // ─── sub / tweeter reactive wave effect ─────────────────────────────
  /** sub_speaker / tweeter_array ごとに「今 ring が出ているか」を保持。
   *  同時 1 ring まで (= 連続発火で ring が無限増殖しないため) のガードに使う。
   *  key は decoration index (= MAP.decorations 内の位置)。 */
  private subWaveActive = new Set<number>();
  private tweeterWaveActive = new Set<number>();
  /** sub / tweeter の wave 発火閾値。spec の > 0.4。 */
  private static readonly SUB_WAVE_THRESHOLD = 0.4;
  private static readonly TWEETER_WAVE_THRESHOLD = 0.4;

  constructor(deps: ClubSceneDeps) {
    super("club");
    this.deps = deps;
    // 照明初期モードは create() で localStorage 読むより前 (= constructor 時点) に
    // 反映する。setupLightingButton が getLightingMode() を読んだ瞬間から正しい値に
    // なる (= button UI と実際の lighting 状態が常に一致)。
    if (deps.initialLightingMode) {
      this.lightingMode = deps.initialLightingMode;
    }
  }

  /** カメラを 1 ステップ拡大 (= モバイルでも見やすく)。上限 ZOOM_MAX で頭打ち。 */
  zoomIn(): void {
    this.setCameraZoom(this.cameraZoom + ClubScene.ZOOM_STEP);
  }

  /** カメラを 1 ステップ縮小。下限 ZOOM_MIN で頭打ち (= マップ全体を見やすい状態を維持)。 */
  zoomOut(): void {
    this.setCameraZoom(this.cameraZoom - ClubScene.ZOOM_STEP);
  }

  private setCameraZoom(z: number): void {
    this.cameraZoom = Phaser.Math.Clamp(z, ClubScene.ZOOM_MIN, ClubScene.ZOOM_MAX);
    this.cameras.main.setZoom(this.cameraZoom);
  }

  preload(): void {
    // AVATARS が空でも問題ない (= 棒人間 fallback)。1 件ずつ load して、
    // 失敗 (404 / decode 失敗) は warn だけ吐いて続行する。
    for (const av of AVATARS) {
      this.load.image(av.key, av.url);
    }
    this.load.on("loaderror", (file: Phaser.Loader.File) => {
      console.warn("[club] asset load failed:", file.src);
    });
  }

  create(): void {
    this.cameras.main.setBounds(0, 0, MAP.world.w, MAP.world.h);
    this.physics.world.setBounds(0, 0, MAP.world.w, MAP.world.h);

    drawFloor(this);
    drawZones(this);
    drawSweetspot(this);
    // 障害物 staticGroup を先に作って drawScreens に渡す (= 各 screen の collider が
    // ここに追加される)。MAP.obstacles の机 / バーカウンターは後段で同じ group に
    // 入る形にする。
    this.obstacles = this.physics.add.staticGroup();
    drawScreens(this, this.deps.live, this.obstacles);
    drawSpeakers(this, this.obstacles);

    // 障害物 (机/バーカウンター) を staticGroup に追加。staticGroup 自体は drawScreens
    // 用に既に初期化済み (= screen collider と同じ group に乗る)。
    // 「上面 (= 物理本体)」+「前面 (= 装飾)」の 2 枚で「箱」を描いて side-view 感を出す。
    // 物理ボディは上面 rect の論理位置を維持 = 当たり判定はトップダウンの座標と一致。
    for (const o of MAP.obstacles) {
      const topColor = o.color ?? 0x4a4a4a;
      const frontColor = darken(topColor, 0.45);
      const topScreen = toScreen(o.x + o.w / 2, o.y + o.h / 2);
      // 上面 (= 物理ボディ)
      const top = this.add.rectangle(topScreen.sx, topScreen.sy, o.w, o.h, topColor);
      top.setStrokeStyle(1, 0x000000, 0.4);
      top.setDepth(o.y + o.h); // 自分の前縁 = 描画 y
      this.obstacles.add(top);
      // 前面 (= 装飾、高さ = obstacle.frontDepth ?? FRONT_DEPTH)。壁を高く見せたい
      // obstacle (= floor↔bar wall) は map.ts 側で frontDepth=60 などに上げる。
      const frontDepth = o.frontDepth ?? FRONT_DEPTH;
      const front = this.add.rectangle(
        topScreen.sx,
        topScreen.sy + o.h / 2 + frontDepth / 2,
        o.w,
        frontDepth,
        frontColor,
      );
      front.setStrokeStyle(1, 0x000000, 0.4);
      front.setDepth(o.y + o.h + 0.1); // 上面の直後
    }

    // DJ デスク上にターンテーブル × 2 + ミキサーを描画 (= 視覚的な「DJ ブース感」)。
    // 位置は map.ts の DJ desk obstacle (= x=700, y=340, w=200, h=50) に直接合わせる。
    drawDjBoothGear(this);

    // バーカウンター天板上のグラス装飾 + 固定 NPC (= バーテンダー等)。
    // depth は y 基準で sort されるので、obstacle (= 机/天板) の後で OK。
    drawDecorations(this);
    drawNPCs(this);

    // 自分のアバター (画像が無ければ棒人間)。halo (足元の光る楕円) で「自分」を強調。
    // 入室前に選択された avatar key を優先、無ければ peer id ハッシュで決定論的に振る。
    const self = this.deps.net.me!;
    const selfAvatarKey =
      this.deps.selfAvatarKey ?? getAvatarForPeerId(self.id)?.key ?? null;
    this.me = makeFigure(
      this,
      self.x,
      self.y,
      self.color,
      formatPeerLabel(self.name || "you", { isSelf: true, listenOnly: self.listenOnly === true }),
      selfAvatarKey,
      { isSelf: true },
    ) as typeof this.me;
    this.physics.world.enable(this.me);
    this.me.body.setSize(22, 58).setOffset(-11, -32);
    this.me.body.setCollideWorldBounds(true);
    this.physics.add.collider(this.me, this.obstacles);
    // Phaser default は 1.0 (即時)。0.12 だと急方向転換で画面酔いしやすかった。
    // 0.28 で反応速度と滑らかさのバランスを取る。
    this.cameras.main.startFollow(this.me, true, 0.28, 0.28);

    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasd = this.input.keyboard!.addKeys("W,A,S,D") as typeof this.wasd;

    // 初期座標を audio (live + net) に同期。これをやらないと scene.update() が
    // 初回 tick するまで net.updatePeerActivation が NaN ガードで no-op になり、
    // 半径内 peer の voice が 1 frame 分 mute される (review: SRE-2 + Game-2)。
    this.deps.live.updatePlayerPos(this.me.x, this.me.y);
    this.deps.net.updatePlayerPos(this.me.x, this.me.y);

    // ピア棒人間 (welcome 直後のピア一覧)
    for (const peer of this.deps.net.peers.values()) this.spawnPeer(peer);

    // 以降のピアイベントは net.h を上書きして scene でハンドリング
    Object.assign(this.deps.net.h, {
      onPeerJoin: (peer: Peer) => this.spawnPeer(peer),
      onPeerMove: (id: string, x: number, y: number) => this.recordPeerSnapshot(id, x, y),
      onPeerLeave: (id: string) => this.removePeer(id),
      onPeerRename: (id: string, name: string) => this.renamePeer(id, name),
    });

    // 入力競合の堅実化: Phaser の topOnly = true は input-enabled なゲームオブジェクト
    // 同士の伝播を制限する設定。pointerdown には直接効かないが、Phaser 内部の
    // hit-test を絞り込めるので念のため有効にしておく。
    this.input.topOnly = true;

    // タップ/クリック移動。キーボードが押された瞬間に target は無効化する (update 内)
    // HUD / topbar の DOM がタップされた場合は canvas に漏らさない。
    // composedPath() の先頭が canvas でない場合は無視する (Shadow DOM / iOS touch
    // で target 書き換えが起きるケースに対する補強)。
    this.input.on("pointerdown", (p: Phaser.Input.Pointer) => {
      if (!isCanvasEvent(p)) return;
      if (uiHit(p)) return;
      const world = this.cameras.main.getWorldPoint(p.x, p.y);
      const logical = fromScreen(world.x, world.y);
      this.target = logical;
      this.stuckMs = 0;
      this.flashTargetMarker(world.x, world.y);
    });

    // 既存 peer がいた場合、spawn 重複なら 1 度だけずらす
    this.maybeAvoidSpawnOverlap();

    // reactive lighting effects (spotlights / mirror / pulses) を生成。
    // GameObject 自体は invisible で作って、setLightingMode で visibility を確定する。
    this.createLightingOverlays();
    // 照明モードは constructor 時点で deps.initialLightingMode から既に this.lightingMode
    // に反映済み。create() で改めて setLightingMode を呼んで visibility を確定する
    // (= "ON にした覚えがないのに照明出てる" / "OFF にしたのに button が ON のまま" の
    // 初期化 race を防ぐ)。
    this.setLightingMode(this.lightingMode);
  }

  update(_time: number, delta: number): void {
    // キーボード入力 → 論理座標方向ベクトル
    let dx = 0;
    let dy = 0;
    if (this.cursors.left?.isDown || this.wasd.A.isDown) dx -= 1;
    if (this.cursors.right?.isDown || this.wasd.D.isDown) dx += 1;
    if (this.cursors.up?.isDown || this.wasd.W.isDown) dy -= 1;
    if (this.cursors.down?.isDown || this.wasd.S.isDown) dy += 1;

    const keyboardActive = dx !== 0 || dy !== 0;
    if (keyboardActive) {
      this.target = null; // キーボードを触ったら自動移動キャンセル
      this.stuckMs = 0;
      const v = rotateInput(dx, dy);
      const mag = Math.hypot(v.dx, v.dy);
      this.me.body.setVelocity((v.dx / mag) * MOVE_SPEED, (v.dy / mag) * MOVE_SPEED);
    } else if (this.target) {
      const tdx = this.target.x - this.me.x;
      const tdy = this.target.y - this.me.y;
      const dist = Math.hypot(tdx, tdy);
      if (dist < ARRIVAL_THRESHOLD) {
        this.me.body.setVelocity(0, 0);
        this.target = null;
        this.stuckMs = 0;
      } else {
        const v = rotateInput(tdx / dist, tdy / dist);
        this.me.body.setVelocity(v.dx * MOVE_SPEED, v.dy * MOVE_SPEED);
        // Pathfinding fallback: 何かに当たっていてかつ実速度が小さいなら、
        // PATHFINDING_STUCK_MS 連続でその状態なら target を諦める (= もがき防止)。
        const blocked = this.me.body.blocked.none ? false : true;
        const realSpeed = Math.hypot(this.me.body.velocity.x, this.me.body.velocity.y);
        if (blocked && realSpeed < MOVE_SPEED - STUCK_SPEED_EPS) {
          this.stuckMs += delta;
          if (this.stuckMs > PATHFINDING_STUCK_MS) {
            this.target = null;
            this.stuckMs = 0;
            this.me.body.setVelocity(0, 0);
          }
        } else {
          this.stuckMs = 0;
        }
      }
    } else {
      this.me.body.setVelocity(0, 0);
      this.stuckMs = 0;
    }

    // soft peer collision: 自分の周辺の peer に対して反発 force を加える。
    // peer は物理 body を持たないので、自分側で位置を見て setVelocity に offset。
    this.applyPeerSoftCollision();

    // 物理移動した後の座標を net に送信 (動いてる or 直後)
    const now = performance.now();
    const speed = Math.abs(this.me.body.velocity.x) + Math.abs(this.me.body.velocity.y);
    const moving = speed > 1;
    if ((moving || now - this.lastSendAt < 200) && now - this.lastSendAt > SEND_INTERVAL_MS) {
      this.deps.net.sendMove(this.me.x, this.me.y);
      this.lastSendAt = now;
    }

    // 空間音響更新 (常に俯瞰系の x, y を渡す)
    this.deps.live.updatePlayerPos(this.me.x, this.me.y);
    this.deps.net.updatePlayerPos(this.me.x, this.me.y);

    // ゾーン判定 (= HUD label 用のみ。zone audio params は live.ts の
    // updateZoneBlend が毎フレーム distance-weighted で連続ブレンドするので
    // ここでの「discrete な zone 切替通知」は audio 経路ではなくなった)。
    // sweetspot は別経路 (= 円形 + グラデーション)。
    const zone = findActiveZone(MAP.zones, this.me.x, this.me.y);
    if (zone !== this.currentZone) {
      this.currentZone = zone;
      this.deps.onZoneChange(zone);
    }

    // sweetspot strength: 円形中心からの距離で 0..1 を計算して毎フレーム更新。
    // 中心ほど 1.0 (= 完全クリア)、半径外で 0 (= 通常音色)。
    // 視覚 (drawSweetspot) は控えめなので、audio 側で「縁から徐々に開いてくる」体感を
    // 出すために **ease-out** を噛ます (= linear * (2 - linear) = 1 - (1-linear)^2)。
    // 縁から少し中に入った時点で軽く効き始め、中心 ±10px で完全。linear だと
    // 中心 ±25px の中だけで一気に変化する急峻なカーブで、視覚控えめだと
    // 「sweet spot がある」を耳で見つけにくかった。
    const ssDx = SWEETSPOT.x - this.me.x;
    const ssDy = SWEETSPOT.y - this.me.y;
    const ssDist = Math.hypot(ssDx, ssDy);
    const linear = Math.max(0, 1 - ssDist / SWEETSPOT.radius);
    const strength = linear * (2 - linear);
    this.deps.live.setSweetspotStrength(strength);

    // peer 描画位置を interpolation buffer から線形補間で更新
    this.renderPeerPositions(now);

    // 発話リング
    updateRing(this.me, this.deps.net.getLocalVolume());
    for (const [id, fig] of this.figures) {
      updateRing(fig, this.deps.net.getPeerVolume(id));
    }

    // Y による描画順 (= 奥にあるものが先に描かれ、手前のキャラクターが上に被る)。
    // 物理は俯瞰だが視覚は side-view 風: 論理 y が大きいほど画面手前 = depth 大。
    this.me.setDepth(this.me.y);
    for (const fig of this.figures.values()) {
      fig.setDepth(fig.y);
    }

    // reactive lighting (= 音域反応 floor 照明)。OFF 時は内部で即 return するので
    // 通常 mode では rAF オーバーヘッドゼロ。
    this.updateLighting();
  }

  // ─── reactive lighting 実装 ────────────────────────────────────────

  /** lighting モードを切り替える (= UI から呼ばれる)。localStorage に保存して
   *  リロード後も維持。OFF にした瞬間に overlay alpha は 0 にリセット。 */
  setLightingMode(mode: "off" | "lite" | "full"): void {
    this.lightingMode = mode;
    const fullEnabled = mode === "full";
    // OFF / Lite では spotlights / mirror dots は invisible。Lite は speaker pulses だけ。
    for (const s of this.spotlights) s.setVisible(fullEnabled);
    for (const d of this.mirrorDots) d.setVisible(fullEnabled);
    if (mode === "off") {
      // envelope state をリセット (= 次の ON 時にゼロから立ち上がる)
      this.bandEnv.bass = 0;
      this.bandEnv.mid = 0;
      this.bandEnv.high = 0;
      this.bandEnv.master = 0;
      this.prevBandEnv.bass = 0;
      this.prevBandEnv.mid = 0;
      this.prevBandEnv.high = 0;
    }
    try {
      localStorage.setItem("__clubLightingMode", mode);
    } catch {
      // localStorage 不在 (= private mode 等) は無視
    }
  }

  getLightingMode(): "off" | "lite" | "full" {
    return this.lightingMode;
  }

  /** 4 枚の overlay を **floor 領域 (= world 座標、floor rect サイズ)** に生成。
   *  default は invisible (= setVisible(false))、GameObject alpha=0 で不可視。
   *  setLightingMode で setVisible(true) されて以降、updateLighting が毎フレーム
   *  setAlpha で透明度を更新する。
   *
   *  **scroll-factor 0 を使わず world 座標** にしてあるので、camera が floor から
   *  離れた場所 (= bar / 通路 / SCREEN 上空) に移動すると overlay も画面外にスクロール
   *  していき、自動的に「floor 上にいる時だけ照明が見える」体感になる。
   *  resize 追従は不要 (= world サイズ固定)。spotlights / mirror dots / pulse は
   *  別途 floorLightingMask で floor rect にクリップして hard guarantee する。
   *
   *  **注意**: Phaser の `add.rectangle(x, y, w, h, color, fillAlpha)` の第 6 引数は
   *  fill の alpha (= 塗りつぶし不透明度)。GameObject alpha とは別の概念で、最終 alpha
   *  は両者の積になる。ここで `fillAlpha = 0` を渡すと、後で setAlpha(0.5) しても
   *  0 × 0.5 = 0 で永久に不可視になる回帰バグが発生する (= 旧実装が踏んでいた)。
   *  なので constructor では fillAlpha 引数を **省略** (= デフォルト 1) し、初期不可視は
   *  GameObject alpha 側の setAlpha(0) で表現する。 */
  private createLightingOverlays(): void {
    // floor 領域を取得して、overlay の世界座標 + サイズを決定する。
    const floor = MAP.zones.find((z) => z.name === "floor");
    if (!floor) return;
    const fx = floor.shape.x;
    const fy = floor.shape.y;
    const fw = floor.shape.w;
    const fh = floor.shape.h;

    // 共有マスク: spotlights / mirror dots / speaker pulses が floor 外にハミ出るのを
    // 物理クリップする。GeometryMask は graphics の塗り潰し領域を mask として使う。
    const maskGfx = this.make.graphics({ x: 0, y: 0 }, false);
    maskGfx.fillStyle(0xffffff);
    maskGfx.fillRect(fx, fy, fw, fh);
    this.floorLightingMask = maskGfx.createGeometryMask();

    // 動的照明: スポットライト + ミラーボール (= world 座標、camera follow 対象、mask 適用)。
    // 旧 v2 にあった「darken / bass / mid / high の 4 枚 フルスクリーン overlay」は
    // 視覚的に flash 過剰で「うるさい」体感だったため撤去。spotlights / mirror dots /
    // speaker pulses の 3 種の **動く照明** だけを採用し、色は band energy で動的に変える。
    this.createSpotlights();
    this.createMirrorDots();
  }

  /** スポットライト 4 つをフロア上空に配置。Full mode のみ可視。
   *  各スポットは sin/cos 駆動でフロアを左右に sweep する。位置 / 透明度 / 色は
   *  updateSpotlights で毎フレーム更新。ADD blend で「白い光が床に当たってる」絵に。
   *  world 座標 (= camera follow 対象) なので player が動くと相対位置が変わる
   *  = 実際のクラブの照明と同じ体感。 */
  private createSpotlights(): void {
    const floor = MAP.zones.find((z) => z.name === "floor");
    if (!floor) return;
    const cx = floor.shape.x + floor.shape.w / 2;
    const cy = floor.shape.y + floor.shape.h / 2;
    for (let i = 0; i < ClubScene.SPOTLIGHT_COUNT; i++) {
      // 楕円: 横長 (= 床に投影された光のコーン)。サイズはフロア幅の 1/4 程度。
      const spot = this.add.ellipse(cx, cy, 280, 160, 0xffffff);
      spot.setBlendMode(Phaser.BlendModes.ADD);
      spot.setAlpha(0);
      spot.setVisible(false);
      // depth はフロア (= -50 程度) より前、character (= y 値) より後ろになるよう
      // フロア中央 y より少し下に設定。1, 2, 3, 4 個目で微妙にズラして互いに被る面白さを残す。
      spot.setDepth(cy - 200 + i);
      // floor 外にハミ出る部分を mask で物理クリップ (= bar/通路に光が漏れない)
      if (this.floorLightingMask) spot.setMask(this.floorLightingMask);
      this.spotlights.push(spot);
    }
  }

  /** ミラーボール反射光 (= 床に散らばる多数の小光点) を初期配置。回転 / 透明度は
   *  updateMirrorDots で毎フレーム更新。各 dot はフロア中心を回る軌道で配置。 */
  private createMirrorDots(): void {
    const floor = MAP.zones.find((z) => z.name === "floor");
    if (!floor) return;
    const cx = floor.shape.x + floor.shape.w / 2;
    const cy = floor.shape.y + floor.shape.h / 2;
    for (let i = 0; i < ClubScene.MIRROR_DOT_COUNT; i++) {
      // 初期位置はフロア中心 (= updateMirrorDots で即上書きされる)
      const dot = this.add.circle(cx, cy, 4, 0xffffff);
      dot.setBlendMode(Phaser.BlendModes.ADD);
      dot.setAlpha(0);
      dot.setVisible(false);
      // floor 外にハミ出る dot は mask で物理クリップ
      if (this.floorLightingMask) dot.setMask(this.floorLightingMask);
      this.mirrorDots.push(dot);
    }
  }

  // 旧 resizeLightingOverlays は overlay を world 座標 + floor サイズに置き換えたため不要。
  // viewport リサイズで overlay 自体のサイズを変える必要はなくなった (= floor rect 固定)。

  /** 毎フレーム呼ばれる lighting tick。OFF なら即 return。 */
  private updateLighting(): void {
    if (this.lightingMode === "off") return;
    const analyser = this.deps.live.getMusicAnalyser?.();
    if (!analyser) return;

    // バッファ再利用 (= frequencyBinCount が変わった時だけ作り直す)
    if (!this.analyserBuf || this.analyserBuf.length !== analyser.frequencyBinCount) {
      this.analyserBuf = new Uint8Array(analyser.frequencyBinCount);
    }
    analyser.getByteFrequencyData(this.analyserBuf);

    // bin 分割 (FFT 512 / 48kHz = bin 0..255、約 94 Hz/bin):
    //  bass: bin 1..6   (~94 .. 564 Hz)   = kick / bass
    //  mid:  bin 7..50  (~656 .. 4688 Hz) = snare / vocal / synth
    //  high: bin 51..200 (~4781 .. 18750 Hz) = hi-hat / cymbal / air
    //  master: 全 bin (bin 0 = DC は除く)
    // 各 bin は 0..255。bin 数で割って 0..1 に正規化。
    const buf = this.analyserBuf;
    const len = buf.length;
    let bassSum = 0, midSum = 0, highSum = 0, masterSum = 0;
    let bassN = 0, midN = 0, highN = 0;
    for (let i = 1; i < 7 && i < len; i++) { bassSum += buf[i] ?? 0; bassN++; }
    for (let i = 7; i < 51 && i < len; i++) { midSum += buf[i] ?? 0; midN++; }
    for (let i = 51; i < 201 && i < len; i++) { highSum += buf[i] ?? 0; highN++; }
    for (let i = 1; i < len; i++) masterSum += buf[i] ?? 0;
    const bassRaw = bassN > 0 ? bassSum / (bassN * 255) : 0;
    const midRaw = midN > 0 ? midSum / (midN * 255) : 0;
    const highRaw = highN > 0 ? highSum / (highN * 255) : 0;
    const masterRaw = len > 1 ? masterSum / ((len - 1) * 255) : 0;

    // Envelope: rise immediate (= 現在値が大きければ更新)、decay slow (= LIGHTING_DECAY)。
    // クラブ照明の慣性を再現 (= 鈍く反応してじわっと消える)。
    const D = ClubScene.LIGHTING_DECAY;
    this.bandEnv.bass = Math.max(bassRaw, this.bandEnv.bass * D);
    this.bandEnv.mid = Math.max(midRaw, this.bandEnv.mid * D);
    this.bandEnv.high = Math.max(highRaw, this.bandEnv.high * D);
    this.bandEnv.master = Math.max(masterRaw, this.bandEnv.master * D);

    // 動的照明エフェクト (= フルスクリーン overlay は撤去済み)
    const now = performance.now();
    // band の rising edge を検出 → speaker からパルス spawn (Lite / Full 両方)
    this.maybeSpawnSpeakerPulses(now);
    // sub_speaker / tweeter_array の reactive wave effect。
    // OFF mode は updateLighting 冒頭で return 済 (= lite/full のみここに来る)。
    // 低域 envelope (bass) → 各 sub_speaker に同心円 ring、
    // 高域 envelope (high) → 各 tweeter_array に halo + spark pulse。
    this.maybeSpawnSubWaves();
    this.maybeSpawnTweeterShimmers();
    // spotlights / mirror ball は Full mode のみ。色 (= computeBandColor) を共有して
    // bass→赤 / mid→緑 / high→青 / 全 band→白 の house DJ プレイ反映。
    if (this.lightingMode === "full") {
      const color = this.computeBandColor();
      this.updateSpotlights(now, color);
      this.updateMirrorDots(now, color);
    }
    this.prevBandEnv.bass = this.bandEnv.bass;
    this.prevBandEnv.mid = this.bandEnv.mid;
    this.prevBandEnv.high = this.bandEnv.high;
  }

  /** band 別 envelope から「現在の色」を算出する。house DJ の low cut / high only /
   *  mid only といったプレイで色が明確に切り替わるよう、**max-normalize** で純色寄り
   *  にする。
   *    - bass=R, mid=G, high=B の RGB マッピング (= 直感的)
   *    - 各 band を max(bass, mid, high) で正規化 → dominant が 255、他が比例
   *    - 輝度は max ベース (= 1.8 倍で saturate)、静音時は灰色フォールバック
   *  サンプル:
   *    bass only      → 純赤 (#ff0000)
   *    mid only       → 純緑 (#00ff00)
   *    high only      → 純青 (#0000ff)
   *    low cut (mid+high) → シアン (#00ffff)
   *    bass + high    → マゼンタ (#ff00ff)
   *    bass + mid     → 黄 (#ffff00)
   *    全帯域         → 白に近い (= フルレンジ)
   */
  private computeBandColor(): number {
    const bass = this.bandEnv.bass;
    const mid = this.bandEnv.mid;
    const high = this.bandEnv.high;
    const max = Math.max(bass, mid, high);
    if (max < 0.05) {
      // 静音時は灰色基調 (= 純黒だと object 消えるので最低限の brightness を残す)
      return 0x404040;
    }
    // dominant band を 255 にして、他は max 比例で減衰 → 純色寄り
    const r = Math.round((bass / max) * 255);
    const g = Math.round((mid / max) * 255);
    const b = Math.round((high / max) * 255);
    // 輝度: dominant band の強度で 0..1。max * 1.8 で 0.55 以上で saturate。
    const brightness = Math.min(1, max * 1.8);
    return Phaser.Display.Color.GetColor(
      Math.round(r * brightness),
      Math.round(g * brightness),
      Math.round(b * brightness),
    );
  }

  /** band 別の rising edge (= bandEnv 急上昇) を検出して対応 speaker からパルスを spawn。
   *  rate-limit (PULSE_MIN_INTERVAL_MS / speaker) で過剰 spawn 防止。
   *  対応:
   *    bass → BL / BR (= 床側サブウーファー想定)
   *    mid → L / R (= メインスタック)
   *    high → C (= 中央/天井)
   *  各 pulse は ADD blend の white-ish circle で expand+fade tween (lifetime ~700ms)。
   */
  private maybeSpawnSpeakerPulses(now: number): void {
    const bands: Array<{
      key: "bass" | "mid" | "high";
      color: number;
      speakerIds: ReadonlyArray<string>;
    }> = [
      { key: "bass", color: 0xff2030, speakerIds: ["BL", "BR"] },
      { key: "mid", color: 0x30ff60, speakerIds: ["L", "R"] },
      { key: "high", color: 0x3060ff, speakerIds: ["C"] },
    ];
    for (const b of bands) {
      const cur = this.bandEnv[b.key];
      const prev = this.prevBandEnv[b.key];
      // rising edge: 前 frame から閾値以上 jump + 現在値が一定強度 (= ノイズフロア除外)
      const delta = cur - prev;
      if (cur < ClubScene.PULSE_TRIGGER_THRESHOLD || delta < 0.12) continue;
      // intensity (= 0.4..1.0) を pulse 半径 + alpha に反映
      const intensity = Math.min(1.0, 0.4 + cur);
      for (const sid of b.speakerIds) {
        const spk = MAP.speakers.find((s) => s.id === sid);
        if (!spk) continue;
        const last = this.pulseLastSpawnAt[sid] ?? 0;
        if (now - last < ClubScene.PULSE_MIN_INTERVAL_MS) continue;
        this.pulseLastSpawnAt[sid] = now;
        this.spawnSpeakerPulse(spk.x, spk.y, b.color, intensity);
      }
    }
  }

  /** 各 sub_speaker decoration を走査して、低域 envelope (bass) > 閾値なら
   *  ring 1 個ぶん発火する。同時 1 ring まで (= 既存 ring が active な
   *  decoration は skip) で過剰増殖を防ぐ。 */
  private maybeSpawnSubWaves(): void {
    const intensity = this.bandEnv.bass;
    if (intensity < ClubScene.SUB_WAVE_THRESHOLD) return;
    for (let i = 0; i < MAP.decorations.length; i++) {
      const dec = MAP.decorations[i];
      if (!dec || dec.type !== "sub_speaker") continue;
      if (this.subWaveActive.has(i)) continue;
      this.flashSubWave(i, dec.x, dec.y, intensity);
    }
  }

  /** 各 tweeter_array decoration を走査して、高域 envelope (high) > 閾値なら
   *  shimmer 1 個ぶん発火する。同時 1 個まで。 */
  private maybeSpawnTweeterShimmers(): void {
    const intensity = this.bandEnv.high;
    if (intensity < ClubScene.TWEETER_WAVE_THRESHOLD) return;
    for (let i = 0; i < MAP.decorations.length; i++) {
      const dec = MAP.decorations[i];
      if (!dec || dec.type !== "tweeter_array") continue;
      if (this.tweeterWaveActive.has(i)) continue;
      this.flashTweeterShimmer(i, dec.x, dec.y, intensity);
    }
  }

  /** sub_speaker から同心円 ring を広げて fade する。
   *  - 半径 20 → 80px に expand、alpha 0.5 → 0、duration 250ms
   *  - 色: 暖色寄り orange-amber (0xff7733)、stroke 2px、塗りなし
   *  - depth: dec.y + 0.5 (= player より下、地面に波紋っぽく)
   *  - 各 sub につき同時 1 ring まで (= subWaveActive Set で gate)。
   *
   *  intensity (0..1) で開始 alpha と final 半径をスケール。 */
  private flashSubWave(decIndex: number, x: number, y: number, intensity: number): void {
    this.subWaveActive.add(decIndex);
    const startR = 20;
    const endR = 80;
    const startAlpha = Math.min(0.7, 0.3 + 0.4 * intensity);
    const ring = this.add.circle(x, y, startR, 0x000000, 0);
    ring.setStrokeStyle(2, 0xff7733, startAlpha);
    ring.setDepth(y + 0.5);
    // scale で半径を表現 (= 円自体の半径は startR 固定、scale で endR/startR 倍にする)。
    // stroke は scale で太くなるが、duration 250ms と短いので体感気にならない。
    this.tweens.add({
      targets: ring,
      scale: endR / startR,
      alpha: 0,
      duration: 250,
      ease: "Cubic.Out",
      onComplete: () => {
        ring.destroy();
        this.subWaveActive.delete(decIndex);
      },
    });
  }

  /** tweeter_array から halo + spark pulse を発火。
   *  - 半径 8 → 16px に expand、alpha 0.7 → 0、duration 180ms
   *  - 色: cool white-blue (0xb8d4ff)、stroke 1.5px
   *  - depth: MAP.world.h + 100 + 1 (= tweeter array 本体の上で発光)
   *  - 同時 1 個まで (= tweeterWaveActive Set で gate)。 */
  private flashTweeterShimmer(decIndex: number, x: number, y: number, intensity: number): void {
    this.tweeterWaveActive.add(decIndex);
    const startR = 8;
    const endR = 16;
    const startAlpha = Math.min(0.85, 0.4 + 0.5 * intensity);
    const halo = this.add.circle(x, y, startR, 0x000000, 0);
    halo.setStrokeStyle(1.5, 0xb8d4ff, startAlpha);
    halo.setBlendMode(Phaser.BlendModes.ADD);
    halo.setDepth(MAP.world.h + 100 + 1);
    this.tweens.add({
      targets: halo,
      scale: endR / startR,
      alpha: 0,
      duration: 180,
      ease: "Cubic.Out",
      onComplete: () => {
        halo.destroy();
        this.tweeterWaveActive.delete(decIndex);
      },
    });
  }

  /** 1 つの円形パルスを生成して 700ms かけて拡大 + フェードアウト → 自動 destroy。
   *  world 座標 (= camera follow 対象、ADD blend)、floor 外は mask でクリップ。 */
  private spawnSpeakerPulse(x: number, y: number, color: number, intensity: number): void {
    const initialRadius = 8;
    const circle = this.add.circle(x, y, initialRadius, color);
    circle.setBlendMode(Phaser.BlendModes.ADD);
    circle.setAlpha(0.7 * intensity);
    circle.setDepth(y); // 床と character の中間に自然 sort
    // floor 外にハミ出る部分は mask でクリップ (= bar / 通路 に光が漏れない)
    if (this.floorLightingMask) circle.setMask(this.floorLightingMask);
    this.tweens.add({
      targets: circle,
      scale: 12 + intensity * 18, // 半径 ~100-300 相当
      alpha: 0,
      duration: 700,
      ease: "Cubic.Out",
      onComplete: () => circle.destroy(),
    });
  }

  /** spotlights を sweep アニメ + bass 強度連動の alpha + band-color 更新。
   *  SPOTLIGHT_COUNT 個が phase をずらしながらフロアを左右に振り回る。
   *  色 (= bass/mid/high 比から算出) は全スポットで共有 → 「クラブのオペレータが
   *  統一して色を振る」体感。旧 HSV 時間 hue rotate は撤去。 */
  private updateSpotlights(now: number, color: number): void {
    if (this.spotlights.length === 0) return;
    const floor = MAP.zones.find((z) => z.name === "floor");
    if (!floor) return;
    const cx = floor.shape.x + floor.shape.w / 2;
    const cy = floor.shape.y + floor.shape.h / 2;
    const halfW = floor.shape.w * 0.42; // フロア端まで届かせない (= 余白)
    const halfH = floor.shape.h * 0.18;
    const bassEnv = this.bandEnv.bass;
    const masterEnv = this.bandEnv.master;
    for (let i = 0; i < this.spotlights.length; i++) {
      const spot = this.spotlights[i];
      if (!spot) continue;
      // 各 spot の phase を 1/N ずつズラす
      const phase = (now / ClubScene.SPOTLIGHT_SWEEP_PERIOD_MS + i / ClubScene.SPOTLIGHT_COUNT) * Math.PI * 2;
      // X は sin で左右、Y は cos で僅かに上下 → 楕円軌道
      const xOff = halfW * Math.sin(phase);
      const yOff = halfH * Math.cos(phase * 1.3 + i);
      spot.setPosition(cx + xOff, cy + yOff);
      // alpha: bass kick 強度 + master の基底光量
      const alpha = Math.min(0.45, 0.08 + bassEnv * 0.3 + masterEnv * 0.1);
      spot.setAlpha(alpha);
      // 色は computeBandColor から共有 (= 全 spot 同一色で統一感)
      spot.fillColor = color;
    }
  }

  /** mirror ball の dot 群を回転 + 透明度 + 色更新。dot 毎に半径 / 開始角度オフセット。
   *  master energy で全 dot の alpha が上下。色は spotlights と共有して統一感を出す。 */
  private updateMirrorDots(now: number, color: number): void {
    if (this.mirrorDots.length === 0) return;
    const floor = MAP.zones.find((z) => z.name === "floor");
    if (!floor) return;
    const cx = floor.shape.x + floor.shape.w / 2;
    const cy = floor.shape.y + floor.shape.h / 2;
    const t = now / ClubScene.MIRROR_ROTATION_PERIOD_MS;
    const masterEnv = this.bandEnv.master;
    const highEnv = this.bandEnv.high;
    for (let i = 0; i < this.mirrorDots.length; i++) {
      const dot = this.mirrorDots[i];
      if (!dot) continue;
      // 各 dot 固有の offset (i ベース) で「等間隔だが微妙にずれる」配置
      const baseAngle = (i / this.mirrorDots.length) * Math.PI * 2 + t * Math.PI * 2;
      // 半径は i に基づき 80-260px、ゆっくり伸縮
      const baseRadius = 80 + 180 * ((i * 0.37) % 1);
      const radius = baseRadius + 30 * Math.sin(t * 2 * Math.PI + i * 0.5);
      const dx = radius * Math.cos(baseAngle);
      // y は半径を 0.6 倍に縮めて「上から見下ろした球」っぽく
      const dy = radius * 0.6 * Math.sin(baseAngle);
      const x = cx + dx;
      const y = cy + dy;
      dot.setPosition(x, y);
      dot.setDepth(y); // y による depth sort
      // alpha: master + high の合成。high が強い時に dots が映える
      const alpha = Math.min(0.85, 0.15 + masterEnv * 0.4 + highEnv * 0.3);
      dot.setAlpha(alpha);
      // 色は spotlights と共有 (= 統一感)
      dot.fillColor = color;
    }
  }

  private spawnPeer(peer: Peer): void {
    if (this.figures.has(peer.id)) return;
    // peer 本人が選択した avatarKey (= サーバ broadcast 経由) を優先。
    // 未指定なら peer.id のハッシュで決定論的にフォールバック (= 旧挙動互換)。
    const avatarKey = peer.avatarKey ?? getAvatarForPeerId(peer.id)?.key ?? null;
    const fig = makeFigure(
      this,
      peer.x,
      peer.y,
      peer.color,
      formatPeerLabel(peer.name || peer.id.slice(0, 6), { listenOnly: peer.listenOnly === true }),
      avatarKey,
    );
    this.figures.set(peer.id, fig);
    // 既存位置を最初の snapshot として登録 (renderPeerPositions で安定する)
    const now = performance.now();
    this.peerBuffers.set(peer.id, [
      { t: now - INTERP_DELAY_MS, x: peer.x, y: peer.y },
      { t: now, x: peer.x, y: peer.y },
    ]);
  }

  /**
   * peer-moved を受信したら snapshot を蓄積する (描画はしない)。
   * 描画は update() の `renderPeerPositions` で `renderTime = now - INTERP_DELAY_MS` の
   * 時刻に該当する位置を 2 つの snapshot から線形補間して行う。
   * これにより 50ms 間隔の peer-moved と描画フレームレートのズレ・network jitter が
   * 吸収され、tween より自然に動く。
   */
  private recordPeerSnapshot(id: string, x: number, y: number): void {
    if (!this.figures.has(id)) return;
    const now = performance.now();
    let buf = this.peerBuffers.get(id);
    if (!buf) {
      buf = [];
      this.peerBuffers.set(id, buf);
    }
    buf.push({ t: now, x, y });
    // 古い snapshot を捨てる (最後の 2 つは必ず残す)
    const cutoff = now - INTERP_BUFFER_MS;
    while (buf.length > 2 && buf[0] && buf[0].t < cutoff) buf.shift();
  }

  private renderPeerPositions(now: number): void {
    const renderTime = now - INTERP_DELAY_MS;
    for (const [id, fig] of this.figures) {
      const buf = this.peerBuffers.get(id);
      if (!buf || buf.length === 0) continue;
      // 通常: renderTime を挟む 2 snapshot を見つけて線形補間
      if (buf.length === 1) {
        const only = buf[0]!;
        fig.x = only.x;
        fig.y = only.y;
        continue;
      }
      // 末尾から逆に走査して renderTime <= b.t となる最初の b と、その 1 つ前 a を採用
      let a: Snapshot | null = null;
      let b: Snapshot | null = null;
      for (let i = buf.length - 1; i >= 1; i--) {
        const cur = buf[i]!;
        if (cur.t >= renderTime) {
          a = buf[i - 1]!;
          b = cur;
        } else {
          break;
        }
      }
      if (!a || !b) {
        // renderTime が buffer 最新より新しい (= 遅延ぶん追いついていない)。
        // 直近 2 snapshot から速度を出して EXTRAPOLATION_CAP_MS まで dead reckoning。
        // buf 長 < 2 や dt <= 0 のときは従来通り最新 snapshot に貼り付き。
        const last = buf[buf.length - 1]!;
        const prev = buf.length >= 2 ? buf[buf.length - 2]! : null;
        if (prev && last.t - prev.t > 0) {
          const dt = last.t - prev.t;
          let vx = (last.x - prev.x) / dt;
          let vy = (last.y - prev.y) / dt;
          // 攻撃者 / 悪質クライアントが 1 snapshot で大きくジャンプする座標を broadcast
          // すると、生 vx/vy で extrapolation すると peer が画面外まで飛ぶ。本来の最大
          // 移動速度 (MOVE_SPEED) より 50% だけ余裕を見て magnitude を clamp する。
          // 通常プレイ (180px/s 直線) なら絶対に踏まないので体感に影響しない。
          const vmag = Math.hypot(vx, vy);
          const vmax = MOVE_SPEED * 1.5;
          if (vmag > vmax) {
            const k = vmax / vmag;
            vx *= k;
            vy *= k;
          }
          const ahead = Math.min(EXTRAPOLATION_CAP_MS, renderTime - last.t);
          fig.x = last.x + vx * ahead;
          fig.y = last.y + vy * ahead;
        } else {
          fig.x = last.x;
          fig.y = last.y;
        }
        continue;
      }
      const span = b.t - a.t;
      const t = span > 0 ? Math.min(1, Math.max(0, (renderTime - a.t) / span)) : 1;
      fig.x = a.x + (b.x - a.x) * t;
      fig.y = a.y + (b.y - a.y) * t;
    }
  }

  /**
   * peer との重なりを 2 段構えで解決する。
   *
   * (1) position resolution: 距離 < R で重なっている peer については、overlap 量に応じて
   *     自分の x/y を直接補正する (= 力の蓄積による overshoot とすり抜けを防ぐ標準テク)。
   *     setVelocity は触らない (動線がブレない)。複数 peer の影響は累算。
   *
   * (2) velocity push: それに加えて、自分の進行方向にいる peer (= dot product > 0) のみ
   *     軽く velocity を押し返す。後方 / 静止中の peer は無視するので、悪意ある peer が
   *     自分の座標に重ねた move を spam しても揺さぶられない (= ストーカー攻撃緩和)。
   *     合計の push は PEER_PUSH_CLAMP で頭打ち。
   */
  private applyPeerSoftCollision(): void {
    if (this.figures.size === 0) return;
    let resolveX = 0;
    let resolveY = 0;
    let pushX = 0;
    let pushY = 0;
    const v = this.me.body.velocity;
    const speed = Math.hypot(v.x, v.y);
    const dirX = speed > 0 ? v.x / speed : 0;
    const dirY = speed > 0 ? v.y / speed : 0;
    for (const fig of this.figures.values()) {
      const ddx = this.me.x - fig.x;
      const ddy = this.me.y - fig.y;
      const d = Math.hypot(ddx, ddy);
      if (d <= 0 || d >= PEER_COLLIDE_RADIUS) continue;
      const nx = ddx / d;
      const ny = ddy / d;
      // position resolution: 重なり量の半分ぶん自分を押し出す
      const overlap = PEER_COLLIDE_RADIUS - d;
      resolveX += nx * overlap * PEER_RESOLVE_FACTOR;
      resolveY += ny * overlap * PEER_RESOLVE_FACTOR;
      // velocity push: 進行方向にある peer のみ (= 自分が peer に「ぶつかりに行ってる」場合)
      // 進行方向 = (dirX, dirY)、peer 方向 = (-nx, -ny)。dot > 0 で「前方の peer」。
      // 静止時 (speed == 0) は velocity push しない (position resolution が肩代わり)。
      if (speed > 0) {
        const dot = dirX * -nx + dirY * -ny;
        if (dot > 0) {
          const intensity = overlap / PEER_COLLIDE_RADIUS;
          pushX += nx * intensity;
          pushY += ny * intensity;
        }
      }
    }
    if (resolveX !== 0 || resolveY !== 0) {
      const nx = this.me.x + resolveX;
      const ny = this.me.y + resolveY;
      // Arcade body の position は container 同期。body.reset は velocity を 0 にして
      // しまうため使わず、x/y 直書きにとどめる (body は次フレームで追従する)。
      this.me.x = nx;
      this.me.y = ny;
    }
    if (pushX !== 0 || pushY !== 0) {
      let pvx = pushX * PEER_COLLIDE_PUSH;
      let pvy = pushY * PEER_COLLIDE_PUSH;
      const pmag = Math.hypot(pvx, pvy);
      if (pmag > PEER_PUSH_CLAMP) {
        const k = PEER_PUSH_CLAMP / pmag;
        pvx *= k;
        pvy *= k;
      }
      this.me.body.setVelocity(v.x + pvx, v.y + pvy);
    }
  }

  /**
   * spawn 直後、既存 peer の SPAWN_AVOID_RADIUS 以内なら最寄りの空き位置に
   * 自動で 1 度だけ移動する。8 方向に SPAWN_AVOID_STEP 刻みで探索し、最初に
   * 「全 peer から SPAWN_AVOID_RADIUS 以上離れた」位置を採用。
   *
   * 候補座標が `MAP.obstacles` の rect 内に入る場合はその候補を弾く。これを
   * 入れないと、攻撃者が peer を obstacle 周辺に並べて被害者を机/カウンター内に
   * テレポートさせられる。全候補 NG なら dispersal を諦めて spawn 位置のまま。
   */
  private maybeAvoidSpawnOverlap(): void {
    if (this.spawnAvoided) return;
    this.spawnAvoided = true;
    const tooClose = (x: number, y: number): boolean => {
      for (const fig of this.figures.values()) {
        if (Math.hypot(x - fig.x, y - fig.y) < SPAWN_AVOID_RADIUS) return true;
      }
      return false;
    };
    const insideObstacle = (x: number, y: number): boolean => {
      for (const o of MAP.obstacles) {
        if (x >= o.x && x <= o.x + o.w && y >= o.y && y <= o.y + o.h) return true;
      }
      return false;
    };
    if (!tooClose(this.me.x, this.me.y)) return;
    // 8 方向 × 3 リング探索 (= 最大 24 候補)
    const dirs = 8;
    for (let ring = 1; ring <= 3; ring++) {
      for (let i = 0; i < dirs; i++) {
        const theta = (i / dirs) * Math.PI * 2;
        const nx = this.me.x + Math.cos(theta) * SPAWN_AVOID_STEP * ring;
        const ny = this.me.y + Math.sin(theta) * SPAWN_AVOID_STEP * ring;
        // ワールド境界内
        if (nx < 20 || nx > MAP.world.w - 20 || ny < 20 || ny > MAP.world.h - 20) continue;
        // 障害物の内部に入る候補は弾く (security: 机/カウンター内テレポート防止)
        if (insideObstacle(nx, ny)) continue;
        if (!tooClose(nx, ny)) {
          this.me.x = nx;
          this.me.y = ny;
          this.me.body.reset(nx, ny);
          this.deps.net.sendMove(nx, ny);
          return;
        }
      }
    }
    // 全候補 NG → 諦める。元の spawn 位置のまま。
  }

  private removePeer(id: string): void {
    const fig = this.figures.get(id);
    if (fig) {
      // destroy 前に走行中の tween を kill しないと Phaser の TweenManager に
      // dangling targets が残って warning や潜在的な leak になる
      this.tweens.killTweensOf(fig);
      fig.destroy();
    }
    this.figures.delete(id);
    this.peerBuffers.delete(id);
  }

  private renamePeer(id: string, name: string): void {
    const fig = this.figures.get(id);
    if (!fig) return;
    const peer = this.deps.net.peers.get(id);
    fig.labelText.setText(formatPeerLabel(name, { listenOnly: peer?.listenOnly === true }));
  }

  /** peer の listenOnly 状態が server broadcast (peer-listen-state) で flip した時に
   *  club.ts から呼ばれる。label の 🔇 suffix を update + voice mesh 参加時には
   *  halo cue を出して「いま声が出始める」を視覚予告する (Game programmer review medium)。 */
  refreshPeerListenLabel(id: string, listenOnly: boolean): void {
    const fig = this.figures.get(id);
    if (!fig) return;
    const peer = this.deps.net.peers.get(id);
    const name = peer?.name || id.slice(0, 6);
    fig.labelText.setText(formatPeerLabel(name, { listenOnly }));
    // listenOnly → false (= 聞き専 → talking 昇格) で voice mesh 参加 cue を出す。
    // halo 1 発と「📢」テキスト 1 発で「声が始まるよ」を予告。
    if (!listenOnly) this.flashVoiceActivation(fig.x, fig.y);
  }

  /** 自分自身の listenOnly 状態が変わった時 (= upgradeToTalking 成功時) に
   *  club.ts から呼ばれる。(you) suffix は維持して 🔇 だけ消す。 */
  refreshSelfListenLabel(listenOnly: boolean): void {
    const self = this.deps.net.me;
    if (!self || !this.me) return;
    this.me.labelText.setText(
      formatPeerLabel(self.name || "you", { isSelf: true, listenOnly }),
    );
  }

  private flashTargetMarker(sx: number, sy: number): void {
    if (this.targetMarker) {
      this.targetMarker.destroy();
      this.targetMarker = null;
    }
    const mk = this.add.circle(sx, sy, 6, 0xffffff, 0.0);
    mk.setStrokeStyle(2, 0xffffff, 0.6);
    this.targetMarker = mk;
    this.tweens.add({
      targets: mk,
      alpha: 0,
      scale: 2.0,
      duration: 280,
      onComplete: () => mk.destroy(),
    });
  }

  /** peer が voice mesh に参加 (= 聞き専 → talking 昇格) した瞬間に頭上に
   *  📢 + halo expansion を 1 発出す。距離が近い peer の声が無前触れで始まる
   *  feel を緩和する (Game programmer review medium)。 */
  private flashVoiceActivation(x: number, y: number): void {
    const sc = toScreen(x, y);
    // 頭上 (= 棒人間の高さぶん上にオフセット) で「📢」テキストが上に流れて fade
    const text = this.add.text(sc.sx, sc.sy - 56, "📢", {
      fontSize: "18px",
    });
    text.setOrigin(0.5).setDepth(y + 50);
    this.tweens.add({
      targets: text,
      y: sc.sy - 88,
      alpha: { from: 1, to: 0 },
      duration: 1400,
      ease: "Sine.easeOut",
      onComplete: () => text.destroy(),
    });
    // halo expansion (= 緑の輪が広がってフェード)
    const halo = this.add.circle(sc.sx, sc.sy - 40, 12, 0x38d27a, 0);
    halo.setStrokeStyle(2, 0x38d27a, 0.7);
    halo.setDepth(y + 49);
    this.tweens.add({
      targets: halo,
      scale: 3.0,
      alpha: { from: 0.7, to: 0 },
      duration: 800,
      onComplete: () => halo.destroy(),
    });
  }
}

// ────────────── 描画ヘルパ ──────────────

function drawFloor(scene: Phaser.Scene): void {
  // side-view 風の床: 奥 (画面上) が暗く、手前 (画面下) が明るい縦グラデーション。
  // 上端付近に「壁」になるよう、最上部だけさらに暗いバンドを置く。
  const wall = scene.add.rectangle(0, 0, MAP.world.w, MAP.world.h, 0x070a0e).setOrigin(0);
  wall.setDepth(-100);

  const floorBand = scene.add.graphics();
  floorBand.fillGradientStyle(0x121620, 0x121620, 0x1c2230, 0x1c2230, 1);
  // 上端から下端まで全部を「床」扱いにし、奥行きグラデで side-view の遠近感を出す
  floorBand.fillRect(0, 0, MAP.world.w, MAP.world.h);
  floorBand.setDepth(-90);

  // 水平のラインを「奥に向かって間隔が狭まる」感じで描いて遠近感を補強。
  // 物理は俯瞰なので位置は線形でいいが、見た目だけ遠くを密に。
  const lines = scene.add.graphics();
  const numLines = 18;
  for (let i = 1; i < numLines; i++) {
    const tNear = i / numLines;
    // 奥 (上) ほど薄く、手前 (下) ほど濃いラインで「板の継ぎ目」感
    const alpha = 0.03 + tNear * 0.06;
    const y = (1 - Math.cos((Math.PI * i) / numLines)) * 0.5 * MAP.world.h;
    lines.lineStyle(1, 0xffffff, alpha);
    lines.beginPath();
    lines.moveTo(0, y);
    lines.lineTo(MAP.world.w, y);
    lines.strokePath();
  }
  lines.setDepth(-80);

  // 水平方向のラインも (奥行きの曲線感を出すため、薄く)
  const vlines = scene.add.graphics();
  vlines.lineStyle(1, 0xffffff, 0.02);
  for (let x = 0; x <= MAP.world.w; x += 200) {
    vlines.beginPath();
    vlines.moveTo(x, 0);
    vlines.lineTo(x, MAP.world.h);
    vlines.strokePath();
  }
  vlines.setDepth(-79);
}

function drawZones(scene: Phaser.Scene): void {
  for (const z of MAP.zones) {
    if (!z.visual) continue;
    const shape = z.shape;
    const center = toScreen(shape.x + shape.w / 2, shape.y + shape.h / 2);
    // ゾーンの床塗り (床より少し前面、obstacle より後ろ)
    const rect = scene.add.rectangle(center.sx, center.sy, shape.w, shape.h, z.visual.fill, z.visual.alpha);
    rect.setDepth(-70);
    if (z.visual.label) {
      // ラベルはゾーンの後縁 (奥) に立てる方が side-view らしく見える
      const labelY = shape.y + 20;
      const labelScreen = toScreen(shape.x + shape.w / 2, labelY);
      const label = scene.add.text(labelScreen.sx, labelScreen.sy, z.visual.label, {
        fontFamily: '-apple-system, "Hiragino Sans", sans-serif',
        fontSize: "20px",
        color: "#ffffff",
        fontStyle: "bold",
      });
      label.setOrigin(0.5).setAlpha(0.5).setDepth(-65);
    }
  }
}

/** computed sweet spot を控えめに描画。中心ほど淡く色がのる + 小さなラベル。
 *  PURE 時代のような hard stroke / pulse animation / tap-shortcut は無し。
 *  天井 tweeter array の HF 全域カバーにより radius が dance floor ほぼ全面まで
 *  拡張されたので、alpha は更に抑えて「広いけど主張しない」見え方にする。 */
function drawSweetspot(scene: Phaser.Scene): void {
  const center = toScreen(SWEETSPOT.x, SWEETSPOT.y);
  // 同心円グラデーション。色は warm white (= floor の cool tone から軽く浮く)。
  // radius 200+ で広いので alpha は元の 1/3 程度に抑える (= 縁 0.005, 中心 ~0.025)。
  const g = scene.add.graphics();
  const rings = 6;
  for (let i = rings; i >= 1; i--) {
    const r = (SWEETSPOT.radius * i) / rings;
    const alpha = 0.005 + (1 - i / rings) * 0.02; // 縁 0.005, 中心 ~0.025
    g.fillStyle(0xfff1c2, alpha);
    g.fillCircle(center.sx, center.sy, r);
  }
  g.setDepth(-70);
  // ラベル: lowercase italic、低 alpha で「あるよ」を示す程度
  const label = scene.add.text(center.sx, center.sy, "sweet spot", {
    fontFamily: '-apple-system, "Hiragino Sans", sans-serif',
    fontSize: "11px",
    color: "#fff1c2",
    fontStyle: "italic",
  });
  label.setOrigin(0.5).setAlpha(0.4).setDepth(-65);
}

function drawScreens(
  scene: Phaser.Scene,
  live: Live,
  obstacles: Phaser.Physics.Arcade.StaticGroup,
): void {
  // 全 screen の (video, placeholder) を集めておき、live から video stream が来たら
  // 同時に全 screen に流し込む (= 同じ放送を複数枠に映す前提)。
  const targets: Array<{
    sc: (typeof MAP.screens)[number];
    video: Phaser.GameObjects.Video;
    placeholder: Phaser.GameObjects.Text;
  }> = [];

  // 内側パディング: body の pink stroke (3px) + 視覚余白 (= 1px) で 4px。
  // 動画はこの inner area に収めて、stroke 上に被って「動画が枠を超えた」見え方を防ぐ。
  const INNER_PAD = 4;
  for (const sc of MAP.screens) {
    const center = toScreen(sc.x + sc.w / 2, sc.y + sc.h / 2);
    const innerW = sc.w - INNER_PAD * 2;
    const innerH = sc.h - INNER_PAD * 2;

    // 衝突判定用の不可視 rect。staticGroup に add すると Arcade Physics の static body
    // が自動的に張られて、プレイヤーが画面の奥 (= スクリーン位置) に進めなくなる。
    // 視覚は body / video / label が担当するので collider は invisible に。
    const collider = scene.add.rectangle(center.sx, center.sy, sc.w, sc.h);
    collider.setVisible(false);
    obstacles.add(collider);

    // 外側のネオン光彩 (= 床より少し奥の depth)
    const glow = scene.add.rectangle(center.sx, center.sy, sc.w + 18, sc.h + 18, 0x9b59b6, 0.22);
    glow.setDepth(sc.y - 1);

    // スクリーン本体 (= 動画が来るまでの暗い背景)
    const body = scene.add.rectangle(center.sx, center.sy, sc.w, sc.h, 0x0a0a18);
    body.setStrokeStyle(3, 0xff4499, 0.85);
    body.setDepth(sc.y);

    // 動画を流すための Video object。
    //   - 初期 displaySize を **innerW × innerH** に明示クランプ (= まだ metadata が来てなくても
    //     描画が body の inner area を絶対に超えない)
    //   - setOrigin(0.5) を明示 (= Phaser 4 のデフォルト変化保険)
    //   - 最初は invisible にして、metadata 到達で aspect 維持の contain fit を再適用
    const video = scene.add.video(center.sx, center.sy);
    video.setOrigin(0.5, 0.5);
    video.setDisplaySize(innerW, innerH);
    video.setDepth(sc.y + 0.05);
    video.setVisible(false);

    // **Mask** で inner area ぴったりにクリップ。video.setDisplaySize の値とは別に
    // 物理的に「これより外には絶対描画しない」保証。Phaser の GeometryMask は
    // Graphics の塗り潰し領域を mask として使う。座標は world (= 0,0 基点) で OK。
    const maskGfx = scene.make.graphics({ x: 0, y: 0 }, false);
    maskGfx.fillStyle(0xffffff);
    maskGfx.fillRect(center.sx - innerW / 2, center.sy - innerH / 2, innerW, innerH);
    video.setMask(maskGfx.createGeometryMask());

    // プレースホルダ文字 (= 動画接続前の状態を視覚化)
    const label = scene.add.text(center.sx, center.sy, sc.label ?? "SCREEN", {
      fontFamily: '-apple-system, "Hiragino Sans", sans-serif',
      fontSize: "28px",
      color: "#ffffff",
      fontStyle: "bold",
    });
    label.setOrigin(0.5).setDepth(sc.y + 0.1).setAlpha(0.55);

    // 配信感を演出する slow pulse
    scene.tweens.add({
      targets: glow,
      alpha: { from: 0.22, to: 0.4 },
      duration: 1700,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });

    targets.push({ sc, video, placeholder: label });
  }

  // WHEP video track が来たときの共通処理。同じ stream を全 screen に流す。
  // 音は live が web audio graph 経由でスピーカー空間化して鳴らしているので、
  // ここでは必ず noAudio = true (= 二重再生防止)。
  const playOnAll = (stream: MediaStream): void => {
    console.info("[club] screen: video stream received, tracks =", stream.getVideoTracks().length);
    for (const t of targets) {
      const { sc, video, placeholder } = t;
      try {
        // Phaser 3.60+ の loadMediaStream は (stream, noAudio?, crossOrigin?) の 3 引数。
        // noAudio=true で <video> の音声出力を抑制 (= web audio graph と二重再生回避)
        video.loadMediaStream(stream, true);
      } catch (e) {
        console.warn("[club] video loadMediaStream failed", e);
        continue;
      }
      // raw HTMLVideoElement に直接 autoplay 緩和の設定を立てる。Phaser は内部の
      // <video> を持っているが、muted / playsInline は loadMediaStream 後に上書き
      // しても safe (= MediaStream は muted な video でも再生継続される仕様)。
      const htmlVideo = (
        video as Phaser.GameObjects.Video & { video?: HTMLVideoElement }
      ).video;
      if (htmlVideo) {
        htmlVideo.muted = true;
        htmlVideo.playsInline = true;
      }
      // Phaser Video.play() 経路で再生開始。これを呼ばないと Phaser 内部の
      // texture upload (= frame を毎フレーム GPU に転送する経路) が起動せず
      // setVisible(true) しても真っ黒のまま。直接 htmlVideo.play() を呼んでも
      // Phaser の texture refresh は走らない (= 必ず Phaser 経由)。
      try {
        video.play();
      } catch (e) {
        console.warn("[club] video.play() failed", e);
      }
      const innerW = sc.w - INNER_PAD * 2;
      const innerH = sc.h - INNER_PAD * 2;
      const fitAndShow = (origin: string): void => {
        // 自然サイズ取得: htmlVideo の videoWidth/Height が最も信頼できる。
        // Phaser の video.width/height は texture サイズ (= 同等の値) のはずだが
        // 4.x で実装が変わって 0 や displayWidth と混同される可能性もあるので
        // 必ず htmlVideo を優先。
        const nw = htmlVideo?.videoWidth || video.width;
        const nh = htmlVideo?.videoHeight || video.height;
        if (!nw || !nh) return;
        const scale = Math.min(innerW / nw, innerH / nh);
        // **setScale + setDisplaySize の belt+suspenders**: Phaser 4 で video texture
        // 更新時に setDisplaySize がリセットされる挙動の対策。scale は texture サイズに
        // 依存しないので safer、加えて displaySize でも上限クランプ。
        video.setScale(scale);
        const targetW = Math.min(innerW, nw * scale);
        const targetH = Math.min(innerH, nh * scale);
        video.setDisplaySize(targetW, targetH);
        video.setVisible(true);
        placeholder.setVisible(false);
        console.info(`[club] screen fit (${origin})`, {
          natural: { w: nw, h: nh },
          scale: scale.toFixed(3),
          display: { w: video.displayWidth, h: video.displayHeight },
          inner: { w: innerW, h: innerH },
        });
      };
      if (htmlVideo) {
        // 多段 listener + 多段 setTimeout で「いつ正しい size が確定するか」のタイミング
        // 揺れを全部カバーする防衛策。loadedmetadata の once は外して reattach も拾う。
        if (htmlVideo.readyState >= 1) fitAndShow("readyState");
        htmlVideo.addEventListener("loadedmetadata", () => fitAndShow("loadedmetadata"));
        htmlVideo.addEventListener("resize", () => fitAndShow("resize"));
        htmlVideo.addEventListener("playing", () => fitAndShow("playing"));
        // texture upload が走った後のサイズリセットを catch するため、
        // 起動直後 + 短い間隔で再適用する (= 100ms/500ms/1500ms)
        setTimeout(() => fitAndShow("t100"), 100);
        setTimeout(() => fitAndShow("t500"), 500);
        setTimeout(() => fitAndShow("t1500"), 1500);
      } else {
        // htmlVideo が取れない (= Phaser 4 で property 名変更されたケース) のフォールバック。
        // この場合は video.width/height でやれるだけやる + 多段 timeout。
        fitAndShow("no-htmlVideo");
        setTimeout(() => fitAndShow("no-htmlVideo-t500"), 500);
      }
    }
  };

  // 後追い登録: live.connect() が scene.create() より先に video track を受けていた
  // 場合は existing stream を同期的に拾う。
  live.onVideoStream = playOnAll;
  const existing = live.getVideoStream();
  if (existing) playOnAll(existing);
}

function drawSpeakers(
  scene: Phaser.Scene,
  obstacles: Phaser.Physics.Arcade.StaticGroup,
): void {
  for (const sp of MAP.speakers) {
    // visible:false の speaker は audio-only (= fan-out virtual)。drawSpeakers の
    // 巨大 PA スタック描画は skip して、視覚は bar / DJ booth / screen を尊重する
    // (Game review HIGH 対応)。
    if (sp.visible === false) continue;
    const screen = toScreen(sp.x, sp.y);
    // 左右壁にぴったりの巨大縦長スタック。論理位置 (sp.y) は足元。
    const STACK_W = 80;
    const STACK_H = 480;

    // 衝突判定 (= 不可視 static body)。speaker の足元 80×24 を踏めない領域に。
    // 巨大スタックの STACK_H=480 全体に当たり判定を貼ると人が回り込めなくなるので、
    // 「スピーカーの底面に shin が当たる」だけの薄い帯にしてある。
    // setOrigin(0.5, 1.0) で「足元基準」にして、sp.y がそのまま底辺の y になる。
    const collider = scene.add.rectangle(screen.sx, screen.sy, STACK_W, 24);
    collider.setOrigin(0.5, 0.5);
    collider.setVisible(false);
    obstacles.add(collider);

    // 本体 (= キャビネット)
    const body = scene.add.rectangle(screen.sx, screen.sy - STACK_H / 2 + 4, STACK_W, STACK_H, 0x18181d);
    body.setStrokeStyle(2, 0xffffff, 0.55);
    body.setDepth(sp.y);

    // ドライバー (= 下から大→中→小、3 段の BOSE 風)
    const woofY = screen.sy - 60;     // 最下段 (大)
    const midY = screen.sy - 200;     // 中段
    const tweetY = screen.sy - 340;   // 最上段 (小)

    const woofRing = scene.add.circle(screen.sx, woofY, 28, 0x101010);
    woofRing.setStrokeStyle(2, 0xaaaaaa, 0.95);
    woofRing.setDepth(sp.y + 0.1);
    const woofCone = scene.add.circle(screen.sx, woofY, 20, 0x1d1d22);
    woofCone.setDepth(sp.y + 0.11);

    const midRing = scene.add.circle(screen.sx, midY, 20, 0x101010);
    midRing.setStrokeStyle(1.5, 0xaaaaaa, 0.9);
    midRing.setDepth(sp.y + 0.1);

    const tweetRing = scene.add.circle(screen.sx, tweetY, 12, 0x101010);
    tweetRing.setStrokeStyle(1.5, 0xaaaaaa, 0.85);
    tweetRing.setDepth(sp.y + 0.1);

    // 発光ハロー (= 縦長スタック全体を覆う脈動)
    const halo = scene.add.circle(screen.sx, screen.sy - STACK_H / 2, 56, 0x000000, 0);
    halo.setStrokeStyle(2, 0x00d4ff, 0.55);
    halo.setDepth(sp.y - 0.1);
    scene.tweens.add({
      targets: halo,
      scale: { from: 1.0, to: 2.0 },
      alpha: { from: 0.7, to: 0 },
      duration: 1400,
      repeat: -1,
      ease: "Quad.Out",
    });

    // ラベル (= スタックの上)
    if (sp.label) {
      const label = scene.add.text(screen.sx, screen.sy - STACK_H - 8, sp.label, {
        fontFamily: '-apple-system, "Hiragino Sans", sans-serif',
        fontSize: "11px",
        color: "#ffffff",
        backgroundColor: "rgba(0,0,0,0.4)",
        padding: { x: 4, y: 1 },
      });
      label.setOrigin(0.5).setAlpha(0.85).setDepth(sp.y + 0.3);
    }
  }
}

/** DJ デスク obstacle (= map.ts の x=550, y=330, w=500, h=90) の上面にプロ DJ ブース機材
 *  一式を描画:
 *    Thorens TD-125 Mk2 × 2 (両端の vinyl turntable)
 *    Pioneer 風 CDJ × 2 (turntable 内側の digital deck)
 *    dope.real 3-band isolator × 1 (UREI の左隣)
 *    UREI 1620 rotary mixer × 1 (中央、伝説の rotary)
 *
 *  **向き**: DJ は desk の北側に立って南 (audience) を向く。各機材の DJ accessible 側
 *  (= ピッチ・ボタン・CD スロット) は **北側**、トーンアーム pivot や VU は **南側**
 *  (= audience に見える側)。
 *
 *  座標は map.ts の DJ desk obstacle に直接連動。obstacle の dim が変わったら
 *  ここも同期更新が必要 (= ハードコード、注意)。 */
function drawDjBoothGear(scene: Phaser.Scene): void {
  // ── DJ desk dim (= map.ts obstacles[0] と同期) ──
  const DESK_X = 550;
  const DESK_Y = 330;
  const DESK_W = 500;
  const DESK_H = 90;
  const DESK_CY = DESK_Y + DESK_H / 2; // = 375、上面の中心 y

  // 機材は desk obstacle の top face (depth = DESK_Y + DESK_H = 420) の直後に描画。
  const GEAR_DEPTH = DESK_Y + DESK_H + 0.5;

  // 機材配置 (= 左から、各機材の間隔 5px):
  //   Thorens A (80) | CDJ A (60) | Isolator (40) | UREI 1620 (90) | CDJ B (60) | Thorens B (80)
  // total: 80+60+40+90+60+80 + 5gaps×5 = 435、 DESK_W=500 で両側 32.5px 余白
  const margin = (DESK_W - (80 + 60 + 40 + 90 + 60 + 80 + 5 * 5)) / 2;
  let cx = DESK_X + margin;
  drawThorensTD125(scene, cx + 40, DESK_CY, GEAR_DEPTH);
  cx += 80 + 5;
  drawCDJ(scene, cx + 30, DESK_CY, GEAR_DEPTH);
  cx += 60 + 5;
  drawDopeRealIsolator(scene, cx + 20, DESK_CY, GEAR_DEPTH);
  cx += 40 + 5;
  drawUREI1620(scene, cx + 45, DESK_CY, GEAR_DEPTH);
  cx += 90 + 5;
  drawCDJ(scene, cx + 30, DESK_CY, GEAR_DEPTH);
  cx += 60 + 5;
  drawThorensTD125(scene, cx + 40, DESK_CY, GEAR_DEPTH);
}

/** Thorens TD-125 Mk2 風ターンテーブル (80×80)。
 *  特徴: 茶色木製プリンス + アルミ トッププレート + 重厚プラッター + SME 3009 風トーンアーム。
 *  向き: DJ は北側 → スピードセレクタは北西 / 電源 LED は北東、トーンアーム pivot は南東。 */
function drawThorensTD125(scene: Phaser.Scene, cx: number, cy: number, depth: number): void {
  const s = toScreen(cx, cy);
  const g = scene.add.graphics();
  g.setDepth(depth);

  // ── 木製プリンス (= ダークウォルナット) ─────────────────
  g.fillStyle(0x4a2e16, 1);
  g.fillRoundedRect(s.sx - 40, s.sy - 40, 80, 80, 3);
  g.lineStyle(1, 0x2a1a0a, 1);
  g.strokeRoundedRect(s.sx - 40, s.sy - 40, 80, 80, 3);
  g.lineStyle(0.5, 0x6a4624, 0.5);
  g.lineBetween(s.sx - 36, s.sy - 36, s.sx + 36, s.sy - 36);
  g.lineBetween(s.sx - 36, s.sy + 36, s.sx + 36, s.sy + 36);

  // ── アルミトッププレート ───────────────────────────────
  g.fillStyle(0x999999, 1);
  g.fillRoundedRect(s.sx - 35, s.sy - 35, 70, 70, 2);
  g.lineStyle(0.5, 0xcccccc, 0.7);
  g.strokeRoundedRect(s.sx - 35, s.sy - 35, 70, 70, 2);
  // brushed metal hint
  g.lineStyle(0.3, 0x777777, 0.4);
  for (let i = -30; i <= 30; i += 4) {
    g.lineBetween(s.sx - 32, s.sy + i, s.sx + 32, s.sy + i);
  }

  // ── プラッター (中央やや西寄り、トーンアーム pivot 用に右側空ける) ──
  const plateCx = s.sx - 3;
  const plateCy = s.sy + 2;
  const plateR = 26;
  g.fillStyle(0x1a1a1a, 1);
  g.fillCircle(plateCx, plateCy, plateR);
  g.lineStyle(0.8, 0x4a4a4a, 0.8);
  g.strokeCircle(plateCx, plateCy, plateR);
  // フェルトマット
  g.fillStyle(0x383838, 1);
  g.fillCircle(plateCx, plateCy, plateR - 2);
  // ストロボピット風リング
  g.lineStyle(0.6, 0x666666, 0.5);
  g.strokeCircle(plateCx, plateCy, plateR - 6);
  g.strokeCircle(plateCx, plateCy, plateR - 11);
  // レコード (ビニール)
  g.fillStyle(0x0a0a0a, 1);
  g.fillCircle(plateCx, plateCy, plateR - 3);
  // 溝
  g.lineStyle(0.3, 0x2a2a2a, 0.7);
  g.strokeCircle(plateCx, plateCy, plateR - 5);
  g.strokeCircle(plateCx, plateCy, plateR - 9);
  g.strokeCircle(plateCx, plateCy, plateR - 14);
  // レーベル
  g.fillStyle(0x205c8a, 1);
  g.fillCircle(plateCx, plateCy, 8);
  g.lineStyle(0.4, 0x4a8cba, 0.8);
  g.strokeCircle(plateCx, plateCy, 8);
  g.fillStyle(0xeae2c8, 0.85);
  g.fillRect(plateCx - 6, plateCy - 0.5, 12, 1);
  // スピンドル
  g.fillStyle(0xdddddd, 1);
  g.fillCircle(plateCx, plateCy, 1.5);

  // ── トーンアーム (SME 3009 風、南東 = rear-right) ──────
  const armPivotX = s.sx + 28;
  const armPivotY = s.sy + 28;
  g.fillStyle(0x1a1a1a, 1);
  g.fillCircle(armPivotX, armPivotY, 5);
  g.lineStyle(0.7, 0xaaaaaa, 0.8);
  g.strokeCircle(armPivotX, armPivotY, 5);
  g.fillStyle(0x3a3a3a, 1);
  g.fillCircle(armPivotX, armPivotY, 2.5);
  // アーム本体
  g.lineStyle(1.6, 0xbbbbbb, 1);
  g.lineBetween(armPivotX - 3, armPivotY - 3, plateCx + 5, plateCy + 2);
  // ヘッドシェル
  g.fillStyle(0xeaeaea, 1);
  g.fillRect(plateCx + 2, plateCy - 1, 6, 4);
  g.lineStyle(0.4, 0x666666, 1);
  g.strokeRect(plateCx + 2, plateCy - 1, 6, 4);
  // 針
  g.lineStyle(0.6, 0x222222, 1);
  g.lineBetween(plateCx + 4, plateCy + 3, plateCx + 4, plateCy + 5);
  // カウンターウェイト
  g.fillStyle(0x222222, 1);
  g.fillCircle(armPivotX + 5, armPivotY + 5, 3);
  g.lineStyle(0.4, 0x666666, 1);
  g.strokeCircle(armPivotX + 5, armPivotY + 5, 3);

  // ── スピードセレクタ 33/45 (= 北西、DJ accessible) ───
  g.fillStyle(0x222222, 1);
  g.fillRoundedRect(s.sx - 32, s.sy - 32, 14, 6, 1);
  g.lineStyle(0.4, 0xaaaaaa, 0.7);
  g.strokeRoundedRect(s.sx - 32, s.sy - 32, 14, 6, 1);
  g.fillStyle(0xff8866, 0.9);
  g.fillCircle(s.sx - 28, s.sy - 29, 1.2);
  g.fillStyle(0x383838, 1);
  g.fillCircle(s.sx - 22, s.sy - 29, 1.2);

  // ── 電源 LED (= 北東、DJ accessible) ─────────────────
  g.fillStyle(0x222222, 1);
  g.fillCircle(s.sx + 26, s.sy - 30, 1.8);
  g.fillStyle(0x6cdd6c, 0.8);
  g.fillCircle(s.sx + 26, s.sy - 30, 1);
}

/** Pioneer 風 CDJ (60×80)。
 *  特徴: 大型ジョグホイール + 上部 CD スロット + 小型 LCD + Cue/Play ボタン + 右側テンポスライダ。
 *  向き: CD スロットとディスプレイは北側 (DJ insert)、ジョグは中央〜南寄り。 */
function drawCDJ(scene: Phaser.Scene, cx: number, cy: number, depth: number): void {
  const s = toScreen(cx, cy);
  const g = scene.add.graphics();
  g.setDepth(depth);

  // ── 本体 ─────────────────────────────────────────────
  g.fillStyle(0x0e0e0e, 1);
  g.fillRoundedRect(s.sx - 30, s.sy - 40, 60, 80, 3);
  g.lineStyle(1, 0x333333, 1);
  g.strokeRoundedRect(s.sx - 30, s.sy - 40, 60, 80, 3);
  g.lineStyle(0.5, 0x4a4a4a, 0.6);
  g.strokeRoundedRect(s.sx - 28, s.sy - 38, 56, 76, 2);

  // ── CD スロット (= 北端、横長細い暗線) ──────────────
  g.fillStyle(0x000000, 1);
  g.fillRoundedRect(s.sx - 22, s.sy - 35, 44, 2, 1);
  g.lineStyle(0.4, 0x333333, 1);
  g.strokeRoundedRect(s.sx - 22, s.sy - 35, 44, 2, 1);
  g.fillStyle(0x2a2a2a, 1);
  g.fillCircle(s.sx + 26, s.sy - 34, 1.5);

  // ── LCD ディスプレイ (= 北寄り、暗いシアン) ──────────
  g.fillStyle(0x081e2a, 1);
  g.fillRect(s.sx - 22, s.sy - 30, 44, 12);
  g.lineStyle(0.5, 0x4a8caa, 0.7);
  g.strokeRect(s.sx - 22, s.sy - 30, 44, 12);
  // 波形 hint
  g.lineStyle(0.5, 0x6acaff, 0.7);
  g.lineBetween(s.sx - 20, s.sy - 24, s.sx + 20, s.sy - 24);
  g.fillStyle(0x9adfff, 0.9);
  // 波形 peak dots (= 決定論的に固定)
  const peaks = [3, 5, 4, 6, 4];
  for (let i = 0; i < 5; i++) {
    g.fillRect(s.sx - 18 + i * 9, s.sy - 27, 1, peaks[i] ?? 4);
  }
  // BPM hint
  g.fillStyle(0xffcc44, 0.85);
  g.fillRect(s.sx - 21, s.sy - 22, 8, 2);

  // ── ホットキューパッド × 4 (= ジョグの北側) ─────────
  const padColors = [0xff4444, 0x44aaff, 0x44dd66, 0xffcc44];
  for (let i = 0; i < 4; i++) {
    g.fillStyle(padColors[i] ?? 0xeeeeee, 0.85);
    g.fillRoundedRect(s.sx - 12 + i * 6, s.sy - 16, 4, 3, 1);
  }

  // ── ジョグホイール (中央〜南寄り、大型) ──────────────
  const jogCx = s.sx;
  const jogCy = s.sy + 6;
  const jogR = 22;
  g.fillStyle(0x161616, 1);
  g.fillCircle(jogCx, jogCy, jogR);
  g.lineStyle(0.8, 0x444444, 1);
  g.strokeCircle(jogCx, jogCy, jogR);
  g.fillStyle(0x2a2a2a, 1);
  g.fillCircle(jogCx, jogCy, jogR - 4);
  // 中央 illumination
  g.fillStyle(0x0a1820, 1);
  g.fillCircle(jogCx, jogCy, jogR - 11);
  g.lineStyle(1, 0xeefcff, 0.9);
  g.lineBetween(jogCx, jogCy, jogCx + (jogR - 12), jogCy);
  g.fillStyle(0x4a4a4a, 1);
  g.fillCircle(jogCx, jogCy, 2);

  // ── キュー / プレイ ボタン (= 南西、DJ accessible) ──
  g.fillStyle(0x442200, 1);
  g.fillRoundedRect(s.sx - 26, s.sy + 32, 10, 5, 1);
  g.fillStyle(0xff8833, 0.85);
  g.fillRoundedRect(s.sx - 25, s.sy + 32.5, 8, 4, 1);
  g.fillStyle(0x004422, 1);
  g.fillRoundedRect(s.sx - 14, s.sy + 32, 10, 5, 1);
  g.fillStyle(0x55ee55, 0.85);
  g.fillRoundedRect(s.sx - 13, s.sy + 32.5, 8, 4, 1);

  // ── テンポスライダ (= 東側、縦) ──────────────────────
  g.fillStyle(0x0a0a0a, 1);
  g.fillRect(s.sx + 21, s.sy - 14, 2, 38);
  g.lineStyle(0.4, 0x666666, 1);
  g.lineBetween(s.sx + 18, s.sy + 5, s.sx + 26, s.sy + 5);
  g.fillStyle(0xe5e5e5, 1);
  g.fillRect(s.sx + 19, s.sy + 3, 6, 4);
}

/** UREI 1620 風ロータリーミキサー (90×80)。
 *  特徴: 木製サイドパネル + 中央黒メタル + 6 ch ロータリー + マスター + VU。
 *  「passive transformer」設計の象徴: フェーダーなし、すべてロータリー。
 *  向き: チャンネルセレクト switch は北側 (DJ accessible)、VU は南側 (audience に見える)。 */
function drawUREI1620(scene: Phaser.Scene, cx: number, cy: number, depth: number): void {
  const s = toScreen(cx, cy);
  const g = scene.add.graphics();
  g.setDepth(depth);

  // ── 木製サイドパネル (= UREI 伝統の brown wood) ──────
  g.fillStyle(0x5a3618, 1);
  g.fillRoundedRect(s.sx - 45, s.sy - 40, 8, 80, 2);
  g.fillRoundedRect(s.sx + 37, s.sy - 40, 8, 80, 2);
  g.lineStyle(0.5, 0x3a2010, 1);
  g.strokeRoundedRect(s.sx - 45, s.sy - 40, 8, 80, 2);
  g.strokeRoundedRect(s.sx + 37, s.sy - 40, 8, 80, 2);

  // ── 中央メタルフェイス ──────────────────────────────
  g.fillStyle(0x161616, 1);
  g.fillRect(s.sx - 37, s.sy - 40, 74, 80);
  g.lineStyle(1, 0x2a2a2a, 1);
  g.strokeRect(s.sx - 37, s.sy - 40, 74, 80);
  // brushed metal hint
  g.lineStyle(0.3, 0x444444, 0.5);
  for (let i = -36; i <= 36; i += 5) {
    g.lineBetween(s.sx - 36, s.sy + i, s.sx + 36, s.sy + i);
  }

  // ── チャンネルセレクト switch × 6 (= 北側、DJ accessible) ──
  for (let i = 0; i < 6; i++) {
    const sx = s.sx - 33 + i * 11;
    g.fillStyle(0x222222, 1);
    g.fillRect(sx, s.sy - 35, 4, 8);
    g.lineStyle(0.4, 0x666666, 1);
    g.strokeRect(sx, s.sy - 35, 4, 8);
    g.fillStyle(0xe5e5e5, 1);
    g.fillRect(sx + 1, s.sy - 33 + (i % 3) * 2, 2, 2);
  }

  // ── 6 ch カラー帯 (= 各 ch の上に細い帯) ─────────────
  const chColors = [0xff5566, 0xff9944, 0xffdd55, 0x66ee66, 0x44aaff, 0xbb66ff];
  for (let i = 0; i < 6; i++) {
    g.fillStyle(chColors[i] ?? 0xffffff, 0.85);
    g.fillRect(s.sx - 33 + i * 11, s.sy - 22, 6, 1);
  }

  // ── 6 ch ロータリーノブ ─────────────────────────────
  for (let i = 0; i < 6; i++) {
    drawLargeKnob(g, s.sx - 30 + i * 11, s.sy - 15);
  }

  // ── マスター ロータリー (= 中央南寄り、金色リング) ───
  const masterCx = s.sx;
  const masterCy = s.sy + 8;
  g.lineStyle(0.8, 0xddaa44, 0.9);
  g.strokeCircle(masterCx, masterCy, 8);
  g.fillStyle(0x2a2a2a, 1);
  g.fillCircle(masterCx, masterCy, 6);
  g.lineStyle(0.5, 0xaaaaaa, 1);
  g.strokeCircle(masterCx, masterCy, 6);
  g.lineStyle(1.2, 0xffeebb, 1);
  g.lineBetween(masterCx, masterCy - 2, masterCx, masterCy - 5.5);

  // ── Booth / Mon ロータリー (= 左右の小ノブ) ──────────
  drawSmallKnob(g, s.sx - 20, s.sy + 10);
  drawSmallKnob(g, s.sx + 20, s.sy + 10);

  // ── 二連 VU メーター (= 南側、L/R) ──────────────────
  drawVuMeter(g, s.sx - 28, s.sy + 24);
  drawVuMeter(g, s.sx + 4, s.sy + 24);

  // ── 電源 LED (= 北西木目境) ──────────────────────────
  g.fillStyle(0x222222, 1);
  g.fillCircle(s.sx - 39, s.sy - 35, 1.5);
  g.fillStyle(0xff8833, 0.85);
  g.fillCircle(s.sx - 39, s.sy - 35, 0.9);

  // ── UREI ロゴ風プレート ──────────────────────────────
  g.fillStyle(0x666666, 1);
  g.fillRect(s.sx - 8, s.sy + 33, 16, 2);
  g.lineStyle(0.3, 0xaaaaaa, 0.7);
  g.strokeRect(s.sx - 8, s.sy + 33, 16, 2);
}

/** dope.real 風 3-band isolator (40×60)。
 *  特徴: 3 大ロータリー (Low/Mid/High) + バイパススイッチ。
 *  向き: バイパスは北側 (DJ accessible)、3 ノブは Low→Mid→High の縦配置。 */
function drawDopeRealIsolator(scene: Phaser.Scene, cx: number, cy: number, depth: number): void {
  const s = toScreen(cx, cy);
  const g = scene.add.graphics();
  g.setDepth(depth);

  // ── 本体 (= 黒メタル、コンパクト) ──────────────────
  g.fillStyle(0x0a0a0a, 1);
  g.fillRoundedRect(s.sx - 20, s.sy - 30, 40, 60, 3);
  g.lineStyle(1, 0x3a3a3a, 1);
  g.strokeRoundedRect(s.sx - 20, s.sy - 30, 40, 60, 3);
  g.lineStyle(0.5, 0x555555, 0.5);
  g.strokeRoundedRect(s.sx - 18, s.sy - 28, 36, 56, 2);

  // ── バイパススイッチ (= 北側、DJ accessible) ─────────
  g.fillStyle(0x222222, 1);
  g.fillRoundedRect(s.sx - 6, s.sy - 26, 12, 5, 1);
  g.lineStyle(0.4, 0x888888, 1);
  g.strokeRoundedRect(s.sx - 6, s.sy - 26, 12, 5, 1);
  g.fillStyle(0xff5544, 0.9);
  g.fillCircle(s.sx + 3, s.sy - 23, 1.2);

  // ── 3 大ロータリーノブ (= 縦配置 Low→Mid→High) ─────
  drawLargeKnob(g, s.sx, s.sy - 12);
  drawBandLabel(g, s.sx - 14, s.sy - 12, "L");
  drawLargeKnob(g, s.sx, s.sy + 3);
  drawBandLabel(g, s.sx - 14, s.sy + 3, "M");
  drawLargeKnob(g, s.sx, s.sy + 18);
  drawBandLabel(g, s.sx - 14, s.sy + 18, "H");

  // ── 各ノブ右側に LED 風 hint ─────────────────────────
  for (let i = 0; i < 3; i++) {
    const y = s.sy - 12 + i * 15;
    g.fillStyle(0x88ddff, 0.7);
    g.fillCircle(s.sx + 11, y - 2, 0.7);
    g.fillStyle(0x88ddff, 0.5);
    g.fillCircle(s.sx + 11, y + 1, 0.7);
  }
}

/** ミキサー用の小型ロータリーノブ (= 直径 ~4px)。 */
function drawKnob(g: Phaser.GameObjects.Graphics, kx: number, ky: number): void {
  g.fillStyle(0x383838, 1);
  g.fillCircle(kx, ky, 2.2);
  g.lineStyle(0.5, 0x888888, 1);
  g.strokeCircle(kx, ky, 2.2);
  g.lineStyle(0.7, 0xeeeeee, 1);
  g.lineBetween(kx, ky - 0.5, kx, ky - 1.9);
}

/** UREI / isolator 用の大型ロータリーノブ (= 直径 ~7px)。 */
function drawLargeKnob(g: Phaser.GameObjects.Graphics, kx: number, ky: number): void {
  g.fillStyle(0x2a2a2a, 1);
  g.fillCircle(kx, ky, 4);
  g.lineStyle(0.6, 0x888888, 1);
  g.strokeCircle(kx, ky, 4);
  g.fillStyle(0x4a4a4a, 1);
  g.fillCircle(kx, ky, 3);
  g.lineStyle(1, 0xf0f0f0, 1);
  g.lineBetween(kx, ky - 0.5, kx, ky - 3.5);
}

/** UREI booth/mon 用の小型ロータリーノブ (= 直径 ~3px)。 */
function drawSmallKnob(g: Phaser.GameObjects.Graphics, kx: number, ky: number): void {
  g.fillStyle(0x3a3a3a, 1);
  g.fillCircle(kx, ky, 2);
  g.lineStyle(0.4, 0x888888, 1);
  g.strokeCircle(kx, ky, 2);
  g.lineStyle(0.6, 0xeeeeee, 1);
  g.lineBetween(kx, ky - 0.3, kx, ky - 1.7);
}

/** UREI 1620 用の VU メーター LED stack (= 8 セグメント横、緑→黄→赤)。 */
function drawVuMeter(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  for (let i = 0; i < 8; i++) {
    const sx = x + i * 3;
    let color: number;
    let alpha: number;
    if (i < 4) { color = 0x55dd55; alpha = 0.85; }
    else if (i < 6) { color = 0xddcc44; alpha = 0.8; }
    else { color = 0xee3333; alpha = i === 6 ? 0.55 : 0.3; }
    g.fillStyle(color, alpha);
    g.fillRect(sx, y, 2, 4);
  }
  g.lineStyle(0.4, 0x444444, 1);
  g.strokeRect(x - 0.5, y - 0.5, 8 * 3, 5);
}

/** isolator バンドラベル (L/M/H) を Graphics で簡易描画 (Text は cost 高いので避ける)。 */
function drawBandLabel(g: Phaser.GameObjects.Graphics, x: number, y: number, ch: "L" | "M" | "H"): void {
  g.fillStyle(0x888888, 0.85);
  if (ch === "L") {
    g.fillRect(x, y - 2, 1, 4);
    g.fillRect(x, y + 1, 3, 1);
  } else if (ch === "M") {
    g.fillRect(x, y - 2, 1, 4);
    g.fillRect(x + 2, y - 2, 1, 4);
    g.fillRect(x + 1, y - 1, 1, 1);
  } else {
    g.fillRect(x, y - 2, 1, 4);
    g.fillRect(x + 2, y - 2, 1, 4);
    g.fillRect(x + 1, y, 1, 1);
  }
}

/** 固定 NPC (= バーテンダー等) を描画。avatar texture が無い場合は silent skip。 */
function drawNPCs(scene: Phaser.Scene): void {
  for (const npc of MAP.npcs) {
    if (!scene.textures.exists(npc.avatarKey)) continue;
    const center = toScreen(npc.x, npc.y);
    const sprite = scene.add.image(center.sx, center.sy, npc.avatarKey);
    const w = sprite.width;
    const h = sprite.height;
    if (h > 0) sprite.setDisplaySize((90 / h) * w, 90);
    // origin (0.5, 1) で論理座標 = 足元
    sprite.setOrigin(0.5, 1);
    sprite.setDepth(npc.y);
    if (npc.label) {
      const label = scene.add
        .text(center.sx, center.sy - 95, npc.label, {
          fontFamily: '-apple-system, "Hiragino Sans", sans-serif',
          fontSize: "11px",
          color: "#ffffff",
          backgroundColor: "rgba(0,0,0,0.4)",
          padding: { x: 4, y: 1 },
        })
        .setOrigin(0.5);
      label.setDepth(npc.y + 0.1).setAlpha(0.85);
    }
  }
}

/** カウンター上のグラスなど装飾を描画。当たり判定なし、視覚のみ。 */
function drawDecorations(scene: Phaser.Scene): void {
  for (const dec of MAP.decorations) {
    const center = toScreen(dec.x, dec.y);
    if (dec.type === "glass") {
      const g = scene.add.graphics();
      // 液体部 (= 上の四角)
      g.fillStyle(0xc8e0f0, 0.7);
      g.fillRect(center.sx - 4, center.sy - 10, 8, 7);
      g.lineStyle(1, 0xffffff, 0.7);
      g.strokeRect(center.sx - 4, center.sy - 10, 8, 7);
      // 茎
      g.lineBetween(center.sx, center.sy - 2, center.sx, center.sy + 3);
      // 台
      g.strokeCircle(center.sx, center.sy + 3, 3);
      // バーカウンター天板 (= obstacle depth) の上に出すため少し前面
      g.setDepth(dec.y + 1);
      continue;
    }
    if (dec.type === "arrow_up") {
      // 床に描いた半透明の上向き矢印 (= 通路で進行方向を示すステンシル)。
      // shaft + arrowhead の単純な「↑」形。控えめな金色で床と馴染ませる。
      const g = scene.add.graphics();
      g.fillStyle(0xffd166, 0.4);
      g.fillRect(center.sx - 5, center.sy - 6, 10, 26); // shaft
      g.fillTriangle(
        center.sx - 18, center.sy - 6,
        center.sx + 18, center.sy - 6,
        center.sx,      center.sy - 26,
      ); // arrowhead
      // 床より少し上、obstacle/sprite (depth=y) より明確に下に置く。
      g.setDepth(-40);
      continue;
    }
    if (dec.type === "bottle") {
      // バーの背面棚に並ぶ酒瓶。色は (x+y) から決定論的に 6 種パレットを循環。
      // body (= 高 18px × 幅 10px) + neck (= 高 6px × 幅 4px) + label band (= 中央に白帯)。
      // depth は dec.y にして bartender sprite (depth=feet y=1005) より前 = bottle が
      // bartender の上に被る心配なし (bottle y=945 < bartender y=1005 なので自然に背景に)。
      const palette: ReadonlyArray<number> = [
        0xa67c52, // whisky / amber
        0x722f37, // wine
        0xe8e8e8, // gin / vodka (clear)
        0xd4a017, // tequila / gold
        0x355e3b, // green liqueur
        0x1a3d6e, // blue
      ];
      const idx = ((dec.x + dec.y * 7) >>> 0) % palette.length;
      const color = palette[idx] ?? 0xa67c52;
      const g = scene.add.graphics();
      // body
      g.fillStyle(color, 0.9);
      g.fillRect(center.sx - 5, center.sy - 12, 10, 15);
      g.lineStyle(1, 0x222222, 0.6);
      g.strokeRect(center.sx - 5, center.sy - 12, 10, 15);
      // neck
      g.fillStyle(color, 0.9);
      g.fillRect(center.sx - 2, center.sy - 18, 4, 6);
      g.strokeRect(center.sx - 2, center.sy - 18, 4, 6);
      // label (= 白い帯)
      g.fillStyle(0xfffaf0, 0.75);
      g.fillRect(center.sx - 5, center.sy - 6, 10, 4);
      g.setDepth(dec.y);
      continue;
    }
    if (dec.type === "sub_speaker") {
      // dance floor 後方に置いた 80×60 のサブウーファー。obstacles 側に同位置 /
      // 同寸の物理 body があるので、ここでは視覚のみ。setOrigin(0.5, 1.0) 相当に
      // 前縁を dec.y に揃える = box は y - 60 .. y を占有 → 物理 body
      // (= 820..880) と整合 (= dec.y=850 のとき視覚 790..850)。
      // ※ obstacle 側の上面 / 前面描画とは別物。obstacle 側は色 0x1a1a1f の素の box
      //   で、ここではその上に「woofer + grill + LED」を更に描いて PA らしくする。
      const SUB_W = 80;
      const SUB_H = 60;
      const topY = center.sy - SUB_H;       // 上端
      const botY = center.sy;                // 前縁 (= dec.y)
      const leftX = center.sx - SUB_W / 2;
      // 本体キャビネット (= dark wood / matte)
      const body = scene.add.rectangle(center.sx, topY + SUB_H / 2, SUB_W, SUB_H, 0x18181d);
      body.setStrokeStyle(2, 0xffffff, 0.4);
      body.setDepth(dec.y);
      // 前面パネル (= grill cloth 風、本体と同寸で更に暗い色)。setOrigin はデフォ (0.5)。
      const front = scene.add.rectangle(center.sx, topY + SUB_H / 2, SUB_W, SUB_H, 0x101015);
      front.setStrokeStyle(1, 0xffffff, 0.2);
      front.setDepth(dec.y + 0.05);
      // 大型 woofer (= 中央に半径 22 の円 = grill ring + cone + dust cap)
      const woofY = topY + SUB_H / 2;
      const ring = scene.add.circle(center.sx, woofY, 22, 0x0a0a0a);
      ring.setStrokeStyle(1.5, 0xaaaaaa, 0.85);
      ring.setDepth(dec.y + 0.1);
      const cone = scene.add.circle(center.sx, woofY, 16, 0x141418);
      cone.setDepth(dec.y + 0.11);
      const dust = scene.add.circle(center.sx, woofY, 5, 0x202024);
      dust.setDepth(dec.y + 0.12);
      // LED 電源 indicator (= 右下角、赤い小さい光 + ハロー)
      const ledX = leftX + SUB_W - 6;
      const ledY = botY - 5;
      const ledHalo = scene.add.circle(ledX, ledY, 3, 0xff3333, 0.6);
      ledHalo.setBlendMode(Phaser.BlendModes.ADD);
      ledHalo.setDepth(dec.y + 0.13);
      const led = scene.add.circle(ledX, ledY, 1.5, 0xff3333);
      led.setDepth(dec.y + 0.14);
      continue;
    }
    if (dec.type === "tweeter_array") {
      // 中央上部に吊り下げた小型 horn 5 個の弧状配列 (= HF を降らせる array)。
      // 「天井から吊られて player figure (depth=y) の上に被る」よう depth を
      // MAP.world.h + 100 に置く (= 全 player より絶対に手前)。
      const ARRAY_DEPTH = MAP.world.h + 100;
      const baseY = center.sy;
      // 吊り紐 3 本 (= base bar の上 18px ぶん、左右と中央)
      const hangG = scene.add.graphics();
      hangG.lineStyle(1, 0x666666, 0.5);
      hangG.lineBetween(center.sx - 40, baseY - 18, center.sx - 40, baseY);
      hangG.lineBetween(center.sx,       baseY - 18, center.sx,       baseY);
      hangG.lineBetween(center.sx + 40, baseY - 18, center.sx + 40, baseY);
      hangG.setDepth(ARRAY_DEPTH);
      // 横桟 (= 100×8 の base bar、horns を吊るす)
      const baseBar = scene.add.rectangle(center.sx, baseY, 100, 8, 0x2a2a30);
      baseBar.setStrokeStyle(1, 0x666666, 0.6);
      baseBar.setDepth(ARRAY_DEPTH);
      // 5 個の horn を base bar の下に弧状 (半径 30、12° 間隔、中心を真下に向ける)
      const RADIUS = 30;
      const STEP = (12 * Math.PI) / 180;
      for (let i = 0; i < 5; i++) {
        const theta = (i - 2) * STEP + Math.PI / 2; // 真下 = π/2、左右に展開
        const hx = center.sx + RADIUS * Math.cos(theta);
        const hy = baseY + RADIUS * Math.sin(theta);
        const horn = scene.add.circle(hx, hy, 6, 0x18181d);
        horn.setStrokeStyle(1.5, 0xaaaaaa, 0.8);
        horn.setDepth(ARRAY_DEPTH);
        const hornCone = scene.add.circle(hx, hy, 4, 0x0a0a0a);
        hornCone.setDepth(ARRAY_DEPTH + 0.01);
      }
      continue;
    }
  }
}

/** アバター画像があれば Sprite、無ければ棒人間 (drawStick) で描画する Container を作る。
 *  - `avatarKey` が指定されていて Phaser texture cache に存在すれば Sprite
 *  - 無ければ (= AVATARS 未設定 / 画像 load 失敗) 既存の drawStick fallback
 *  - `options.isSelf` のとき足元に薄い金色の halo + パルス tween を追加 (= 自分の強調)
 */
function makeFigure(
  scene: Phaser.Scene,
  x: number,
  y: number,
  color: string,
  label: string,
  avatarKey: string | null,
  options: { isSelf?: boolean } = {},
): FigureContainer {
  const colorNum = Phaser.Display.Color.HexStringToColor(color).color;

  // 自分の足元に光る楕円 halo を置く。Container の一番後ろに追加して body の下に来る。
  let halo: Phaser.GameObjects.Graphics | null = null;
  if (options.isSelf) {
    halo = scene.add.graphics();
    halo.fillStyle(0xffd700, 0.18);
    halo.fillEllipse(0, 30, 70, 22);
    halo.lineStyle(2, 0xffd700, 0.5);
    halo.strokeEllipse(0, 30, 70, 22);
  }

  // 発話 ring (= AnalyserNode level に応じて updateRing で表示/非表示が切り替わる)
  const ring = scene.add.graphics();
  ring.setVisible(false);

  // 本体: アバター画像が load 済みなら Sprite、無ければ棒人間 fallback
  let body: Phaser.GameObjects.Image | Phaser.GameObjects.Graphics;
  let labelY: number;
  if (avatarKey && scene.textures.exists(avatarKey)) {
    const sprite = scene.add.image(0, 0, avatarKey);
    // 表示高さを 90px に固定 (= chibi 比率、棒人間サイズと近い)。
    // アスペクト比は維持。
    const w = sprite.width;
    const h = sprite.height;
    if (h > 0) sprite.setDisplaySize((90 / h) * w, 90);
    // 足元 (画像底辺) を Container 中心の y=+25 あたりに置く。
    // = 棒人間の足元 (y=+26) と揃えて depth sort / 障害物との見た目が馴染む。
    sprite.setOrigin(0.5, 1);
    sprite.y = 25;
    body = sprite;
    // sprite 上端 ≒ y=-65 なので、label はその更に上
    labelY = -78;
  } else {
    const g = scene.add.graphics();
    drawStick(g, colorNum);
    body = g;
    labelY = -52;
  }

  const labelText = scene.add
    .text(0, labelY, label, {
      fontFamily: '-apple-system, "Hiragino Sans", sans-serif',
      fontSize: "12px",
      color: "#ffffff",
      backgroundColor: "rgba(0,0,0,0.35)",
      padding: { x: 4, y: 2 },
    })
    .setOrigin(0.5);

  // children (描画順: 後ろ → 前): halo → ring → body → label
  const children: Phaser.GameObjects.GameObject[] = [];
  if (halo) children.push(halo);
  children.push(ring, body, labelText);

  const container = scene.add.container(x, y, children) as FigureContainer;
  container.labelText = labelText;
  container.ring = ring;
  container.halo = halo;

  // 自分の halo をパルス (= 1 秒周期で scale 1.0 ↔ 1.15)。Phaser tween で持続再生。
  if (halo) {
    scene.tweens.add({
      targets: halo,
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  return container;
}

function drawStick(g: Phaser.GameObjects.Graphics, color: number): void {
  g.lineStyle(2.5, color, 1);
  g.strokeCircle(0, -22, 9);
  g.beginPath();
  g.moveTo(0, -13);
  g.lineTo(0, 12);
  g.moveTo(-11, -4);
  g.lineTo(11, -4);
  g.moveTo(0, 12);
  g.lineTo(-9, 26);
  g.moveTo(0, 12);
  g.lineTo(9, 26);
  g.strokePath();
}

function updateRing(container: FigureContainer, level: number): void {
  const ring = container.ring;
  if (level < 0.04) {
    ring.setVisible(false);
    return;
  }
  const intensity = Math.min(1, level * 2.5);
  const scale = 1 + intensity * 0.6;
  ring.clear();
  ring.lineStyle(2.5, 0x27ae60, 0.4 + intensity * 0.5);
  ring.strokeEllipse(0, 30, 36 * scale, 14 * scale);
  ring.setVisible(true);
}

/**
 * pointerdown が canvas 由来かを composedPath() で確認する。
 * Shadow DOM や iOS Safari の touch では event.target が canvas に置き換わるケースが
 * あるが、composedPath() は実際に発火した要素ツリーを返すので堅い。
 * 最初の要素 (= leaf-most) が canvas でない場合は UI 要素上でのタップとみなす。
 */
function isCanvasEvent(p: Phaser.Input.Pointer): boolean {
  const ev = p.event as Event | undefined;
  if (!ev) return true; // 念のため fail-open。通常は必ずある。
  // composedPath() は IE11 以外で広く対応。Safari 11+ / iOS 11+ で OK。
  const path =
    typeof (ev as Event & { composedPath?: () => EventTarget[] }).composedPath === "function"
      ? (ev as Event & { composedPath: () => EventTarget[] }).composedPath()
      : null;
  if (!path || path.length === 0) return true; // fallback
  const first = path[0] as Element | undefined;
  if (!first || typeof (first as Element).tagName !== "string") return true;
  return (first as Element).tagName === "CANVAS";
}

// ミュートボタンや HUD の上をクリックした時は Phaser に伝播させない判定。
// id 付きの UI 要素を覆うように DOM をオーバーレイしているので、pointerdown のターゲットを見る。
function uiHit(p: Phaser.Input.Pointer): boolean {
  const target = p.event?.target as HTMLElement | undefined;
  if (!target) return false;
  return Boolean(target.closest("#club-mute, #club-hud, .club-topbar, #club-overlay"));
}

/** label の suffix を組み立てる。`(you)` は自分のみ、`🔇` は listenOnly のみ。
 *  順序は `name (you) 🔇` で固定 → rename / listen-state 変更時にも同じ形を保てる。 */
function formatPeerLabel(
  name: string,
  opts: { isSelf?: boolean; listenOnly: boolean },
): string {
  const you = opts.isSelf ? " (you)" : "";
  const mute = opts.listenOnly ? " 🔇" : "";
  return `${name}${you}${mute}`;
}

/** 0xRRGGBB の色を amount (0..1) ぶん暗くする。前面の影色用 */
function darken(color: number, amount: number): number {
  const r = Math.floor(((color >> 16) & 0xff) * (1 - amount));
  const g = Math.floor(((color >> 8) & 0xff) * (1 - amount));
  const b = Math.floor((color & 0xff) * (1 - amount));
  return (r << 16) | (g << 8) | b;
}
